// Shared constants + helpers used by the Visualiser sub-components.

// Pre-generated room renders exist for all 24 Collection products.
// Any (productId, roomKey) with a matching JPG under /public/visualizer/
// will display as a photorealistic room; otherwise we fall back to the
// swatch image.
export const RENDERED = new Set([
  // Timber
  "tmb-luxplank",
  "tmb-versailles-parquet", "tmb-euro-oak-natural", "tmb-euro-oak-smoked",
  "tmb-walnut-herringbone", "tmb-oak-chevron", "tmb-blackbutt",
  // Carpet
  "cpt-bremworth-galet-skarn", "cpt-highland-loop", "cpt-sisal-hybrid",
  "cpt-cashmere-sublime", "cpt-berber-loop", "cpt-sisal-natural",
  // Tile
  "tle-botticino", "tle-calacatta-slab", "tle-terrazzo", "tle-zellige",
  "tle-concrete-porcelain", "tle-herringbone-subway",
  // Stone
  "stn-taj-mahal-quartzite", "stn-travertine-crazy", "stn-honed-limestone",
  "stn-marble-bookmatch", "stn-basaltina", "stn-bluestone",
]);

export const ELEMENTS = ["Timber", "Carpet", "Tile", "Stone"];

// LRU cap so long sessions don't balloon memory. Each cached render is a
// blob URL (references memory-held Blob ~600KB); we revoke evicted URLs.
export const AI_CACHE_MAX = 12;

/**
 * Trim an object-backed LRU cache down to the `max` most-recent entries.
 * Callers pass an optional `onEvict(key, value)` so blob URLs can be revoked.
 */
export function trimCache(obj, max, onEvict) {
  const entries = Object.entries(obj);
  if (entries.length <= max) return obj;
  const drop = entries.slice(0, entries.length - max);
  if (onEvict) drop.forEach(([k, v]) => onEvict(k, v));
  return Object.fromEntries(entries.slice(-max));
}

// Fetch a public image URL and return base64 (no data: prefix).
// Results are memoised by URL — colour swaps on the same room won't refetch.
// The result is *downscaled to ~1200px longest edge* + re-encoded as JPEG q=82
// so the AI upload payload is small (fast transfer, faster server processing).
const b64Cache = new Map();
export async function urlToB64(url) {
  if (b64Cache.has(url)) return b64Cache.get(url);
  const p = (async () => {
    const res = await fetch(url);
    const blob = await res.blob();
    // Decode into a bitmap, downscale, re-encode to compact JPEG.
    const bitmap = await createImageBitmap(blob);
    const MAX = 1200;
    const scale = Math.min(1, MAX / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const outBlob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.82));
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result).split(",")[1]);
      fr.onerror = reject;
      fr.readAsDataURL(outBlob);
    });
  })();
  b64Cache.set(url, p);
  return p;
}

/**
 * Turn a base64 image payload into a blob URL. Blob URLs are ~33% smaller
 * in memory than base64 data URLs and decode faster in the browser.
 * Caller MUST URL.revokeObjectURL(url) when it's no longer displayed.
 */
export function b64ToBlobUrl(mime, b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: mime }));
}

export const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Pre-rendered bathroom colour combinations (generated by
// scripts/gen_bathroom_colours.py — do not hand-edit).
export const BATH_COLOUR_RENDERED = new Set([
  "stn-basaltina-c0-wall-stn-basaltina-c1",
  "stn-basaltina-c0-wall-stn-basaltina-c2",
  "stn-basaltina-c0-wall-stn-basaltina-c3",
  "stn-basaltina-c0-wall-stn-basaltina-c4",
  "stn-basaltina-c0-wall-stn-bluestone-c1",
  "stn-basaltina-c0-wall-stn-bluestone-c2",
  "stn-basaltina-c0-wall-stn-bluestone-c3",
  "stn-basaltina-c0-wall-stn-bluestone-c4",
  "stn-basaltina-c0-wall-stn-honed-limestone-c1",
  "stn-basaltina-c0-wall-stn-honed-limestone-c2",
  "stn-basaltina-c0-wall-stn-honed-limestone-c3",
  "stn-basaltina-c0-wall-stn-honed-limestone-c4",
  "stn-basaltina-c0-wall-stn-marble-bookmatch-c1",
  "stn-basaltina-c0-wall-stn-marble-bookmatch-c2",
  "stn-basaltina-c0-wall-stn-marble-bookmatch-c3",
  "stn-basaltina-c0-wall-stn-marble-bookmatch-c4",
  "stn-basaltina-c0-wall-stn-taj-mahal-quartzite-c1",
  "stn-basaltina-c0-wall-stn-taj-mahal-quartzite-c2",
  "stn-basaltina-c0-wall-stn-taj-mahal-quartzite-c3",
  "stn-basaltina-c0-wall-stn-taj-mahal-quartzite-c4",
  "stn-basaltina-c0-wall-stn-travertine-crazy-c1",
  "stn-basaltina-c0-wall-stn-travertine-crazy-c2",
  "stn-basaltina-c0-wall-stn-travertine-crazy-c3",
  "stn-basaltina-c0-wall-stn-travertine-crazy-c4",
  "stn-basaltina-c0-wall-tle-botticino-c1",
  "stn-basaltina-c0-wall-tle-botticino-c2",
  "stn-basaltina-c0-wall-tle-botticino-c3",
  "stn-basaltina-c0-wall-tle-botticino-c4",
  "stn-basaltina-c0-wall-tle-calacatta-slab-c1",
  "stn-basaltina-c0-wall-tle-calacatta-slab-c2",
  "stn-basaltina-c0-wall-tle-calacatta-slab-c3",
  "stn-basaltina-c0-wall-tle-calacatta-slab-c4",
  "stn-basaltina-c0-wall-tle-concrete-porcelain-c1",
  "stn-basaltina-c0-wall-tle-concrete-porcelain-c2",
  "stn-basaltina-c0-wall-tle-concrete-porcelain-c3",
  "stn-basaltina-c0-wall-tle-concrete-porcelain-c4",
  "stn-basaltina-c0-wall-tle-herringbone-subway-c1",
  "stn-basaltina-c0-wall-tle-herringbone-subway-c2",
  "stn-basaltina-c0-wall-tle-herringbone-subway-c3",
  "stn-basaltina-c0-wall-tle-herringbone-subway-c4",
  "stn-basaltina-c0-wall-tle-terrazzo-c1",
  "stn-basaltina-c0-wall-tle-terrazzo-c2",
  "stn-basaltina-c0-wall-tle-terrazzo-c3",
  "stn-basaltina-c0-wall-tle-terrazzo-c4",
  "stn-basaltina-c0-wall-tle-zellige-c1",
  "stn-basaltina-c0-wall-tle-zellige-c2",
  "stn-basaltina-c0-wall-tle-zellige-c3",
  "stn-basaltina-c0-wall-tle-zellige-c4",
  "stn-basaltina-c1-wall-stn-basaltina-c0",
  "stn-basaltina-c1-wall-stn-basaltina-c1",
  "stn-basaltina-c1-wall-stn-basaltina-c2",
  "stn-basaltina-c1-wall-stn-basaltina-c3",
  "stn-basaltina-c1-wall-stn-basaltina-c4",
  "stn-basaltina-c1-wall-stn-bluestone-c0",
  "stn-basaltina-c1-wall-stn-bluestone-c1",
  "stn-basaltina-c1-wall-stn-bluestone-c2",
  "stn-basaltina-c1-wall-stn-bluestone-c3",
  "stn-basaltina-c1-wall-stn-bluestone-c4",
  "stn-basaltina-c1-wall-stn-honed-limestone-c0",
  "stn-basaltina-c1-wall-stn-honed-limestone-c1",
  "stn-basaltina-c1-wall-stn-honed-limestone-c2",
  "stn-basaltina-c1-wall-stn-honed-limestone-c3",
  "stn-basaltina-c1-wall-stn-honed-limestone-c4",
  "stn-basaltina-c1-wall-stn-marble-bookmatch-c0",
  "stn-basaltina-c1-wall-stn-marble-bookmatch-c1",
  "stn-basaltina-c1-wall-stn-marble-bookmatch-c2",
  "stn-basaltina-c1-wall-stn-marble-bookmatch-c3",
  "stn-basaltina-c1-wall-stn-marble-bookmatch-c4",
  "stn-basaltina-c1-wall-stn-taj-mahal-quartzite-c0",
  "stn-basaltina-c1-wall-stn-taj-mahal-quartzite-c1",
  "stn-basaltina-c1-wall-stn-taj-mahal-quartzite-c2",
  "stn-basaltina-c1-wall-stn-taj-mahal-quartzite-c3",
  "stn-basaltina-c1-wall-stn-taj-mahal-quartzite-c4",
  "stn-basaltina-c1-wall-stn-travertine-crazy-c0",
  "stn-basaltina-c1-wall-stn-travertine-crazy-c1",
  "stn-basaltina-c1-wall-stn-travertine-crazy-c2",
  "stn-basaltina-c1-wall-stn-travertine-crazy-c3",
  "stn-basaltina-c1-wall-stn-travertine-crazy-c4",
  "stn-basaltina-c1-wall-tle-botticino-c0",
  "stn-basaltina-c1-wall-tle-botticino-c1",
  "stn-basaltina-c1-wall-tle-botticino-c2",
  "stn-basaltina-c1-wall-tle-botticino-c3",
  "stn-basaltina-c1-wall-tle-botticino-c4",
  "stn-basaltina-c1-wall-tle-calacatta-slab-c0",
  "stn-basaltina-c1-wall-tle-calacatta-slab-c1",
  "stn-basaltina-c1-wall-tle-calacatta-slab-c2",
  "stn-basaltina-c1-wall-tle-calacatta-slab-c3",
  "stn-basaltina-c1-wall-tle-calacatta-slab-c4",
  "stn-basaltina-c1-wall-tle-concrete-porcelain-c0",
  "stn-basaltina-c1-wall-tle-concrete-porcelain-c1",
  "stn-basaltina-c1-wall-tle-concrete-porcelain-c2",
  "stn-basaltina-c1-wall-tle-concrete-porcelain-c3",
  "stn-basaltina-c1-wall-tle-concrete-porcelain-c4",
  "stn-basaltina-c1-wall-tle-herringbone-subway-c0",
  "stn-basaltina-c1-wall-tle-herringbone-subway-c1",
  "stn-basaltina-c1-wall-tle-herringbone-subway-c2",
  "stn-basaltina-c1-wall-tle-herringbone-subway-c3",
  "stn-basaltina-c1-wall-tle-herringbone-subway-c4",
  "stn-basaltina-c1-wall-tle-terrazzo-c0",
  "stn-basaltina-c1-wall-tle-terrazzo-c1",
  "stn-basaltina-c1-wall-tle-terrazzo-c2",
  "stn-basaltina-c1-wall-tle-terrazzo-c3",
  "stn-basaltina-c1-wall-tle-terrazzo-c4",
  "stn-basaltina-c1-wall-tle-zellige-c0",
  "stn-basaltina-c1-wall-tle-zellige-c1",
  "stn-basaltina-c1-wall-tle-zellige-c2",
  "stn-basaltina-c1-wall-tle-zellige-c3",
  "stn-basaltina-c1-wall-tle-zellige-c4",
  "stn-basaltina-c2-wall-stn-basaltina-c0",
  "stn-basaltina-c2-wall-stn-basaltina-c1",
  "stn-basaltina-c2-wall-stn-basaltina-c2",
  "stn-basaltina-c2-wall-stn-basaltina-c3",
  "stn-basaltina-c2-wall-stn-basaltina-c4",
  "stn-basaltina-c2-wall-stn-bluestone-c0",
  "stn-basaltina-c2-wall-stn-bluestone-c1",
  "stn-basaltina-c2-wall-stn-bluestone-c2",
  "stn-basaltina-c2-wall-stn-bluestone-c3",
  "stn-basaltina-c2-wall-stn-bluestone-c4",
  "stn-basaltina-c2-wall-stn-honed-limestone-c0",
  "stn-basaltina-c2-wall-stn-honed-limestone-c1",
  "stn-basaltina-c2-wall-stn-honed-limestone-c2",
  "stn-basaltina-c2-wall-stn-honed-limestone-c3",
  "stn-basaltina-c2-wall-stn-honed-limestone-c4",
  "stn-basaltina-c2-wall-stn-marble-bookmatch-c0",
  "stn-basaltina-c2-wall-stn-marble-bookmatch-c1",
  "stn-basaltina-c2-wall-stn-marble-bookmatch-c2",
  "stn-basaltina-c2-wall-stn-marble-bookmatch-c3",
  "stn-basaltina-c2-wall-stn-marble-bookmatch-c4",
  "stn-basaltina-c2-wall-stn-taj-mahal-quartzite-c0",
  "stn-basaltina-c2-wall-stn-taj-mahal-quartzite-c1",
  "stn-basaltina-c2-wall-stn-taj-mahal-quartzite-c2",
  "stn-basaltina-c2-wall-stn-taj-mahal-quartzite-c3",
  "stn-basaltina-c2-wall-stn-taj-mahal-quartzite-c4",
  "stn-basaltina-c2-wall-stn-travertine-crazy-c0",
  "stn-basaltina-c2-wall-stn-travertine-crazy-c1",
  "stn-basaltina-c2-wall-stn-travertine-crazy-c2",
  "stn-basaltina-c2-wall-stn-travertine-crazy-c3",
  "stn-basaltina-c2-wall-stn-travertine-crazy-c4",
  "stn-basaltina-c2-wall-tle-botticino-c0",
  "stn-basaltina-c2-wall-tle-botticino-c1",
  "stn-basaltina-c2-wall-tle-botticino-c2",
  "stn-basaltina-c2-wall-tle-botticino-c3",
  "stn-basaltina-c2-wall-tle-botticino-c4",
  "stn-basaltina-c2-wall-tle-calacatta-slab-c0",
  "stn-basaltina-c2-wall-tle-calacatta-slab-c1",
  "stn-basaltina-c2-wall-tle-calacatta-slab-c2",
  "stn-basaltina-c2-wall-tle-calacatta-slab-c3",
  "stn-basaltina-c2-wall-tle-calacatta-slab-c4",
  "stn-basaltina-c2-wall-tle-concrete-porcelain-c0",
  "stn-basaltina-c2-wall-tle-concrete-porcelain-c1",
  "stn-basaltina-c2-wall-tle-concrete-porcelain-c2",
  "stn-basaltina-c2-wall-tle-concrete-porcelain-c3",
  "stn-basaltina-c2-wall-tle-concrete-porcelain-c4",
  "stn-basaltina-c2-wall-tle-herringbone-subway-c0",
  "stn-basaltina-c2-wall-tle-herringbone-subway-c1",
  "stn-basaltina-c2-wall-tle-herringbone-subway-c2",
  "stn-basaltina-c2-wall-tle-herringbone-subway-c3",
  "stn-basaltina-c2-wall-tle-herringbone-subway-c4",
  "stn-basaltina-c2-wall-tle-terrazzo-c0",
  "stn-basaltina-c2-wall-tle-terrazzo-c1",
  "stn-basaltina-c2-wall-tle-terrazzo-c2",
  "stn-basaltina-c2-wall-tle-terrazzo-c3",
  "stn-basaltina-c2-wall-tle-terrazzo-c4",
  "stn-basaltina-c2-wall-tle-zellige-c0",
  "stn-basaltina-c2-wall-tle-zellige-c1",
  "stn-basaltina-c2-wall-tle-zellige-c2",
  "stn-basaltina-c2-wall-tle-zellige-c3",
  "stn-basaltina-c2-wall-tle-zellige-c4",
  "stn-basaltina-c3-wall-stn-basaltina-c0",
  "stn-basaltina-c3-wall-stn-basaltina-c1",
  "stn-basaltina-c3-wall-stn-basaltina-c2",
  "stn-basaltina-c3-wall-stn-basaltina-c3",
  "stn-basaltina-c3-wall-stn-basaltina-c4",
  "stn-basaltina-c3-wall-stn-bluestone-c0",
  "stn-basaltina-c3-wall-stn-bluestone-c1",
  "stn-basaltina-c3-wall-stn-bluestone-c2",
  "stn-basaltina-c3-wall-stn-bluestone-c3",
  "stn-basaltina-c3-wall-stn-bluestone-c4",
  "stn-basaltina-c3-wall-stn-honed-limestone-c0",
  "stn-basaltina-c3-wall-stn-honed-limestone-c1",
  "stn-basaltina-c3-wall-stn-honed-limestone-c2",
  "stn-basaltina-c3-wall-stn-honed-limestone-c3",
  "stn-basaltina-c3-wall-stn-honed-limestone-c4",
  "stn-basaltina-c3-wall-stn-marble-bookmatch-c0",
  "stn-basaltina-c3-wall-stn-marble-bookmatch-c1",
  "stn-basaltina-c3-wall-stn-marble-bookmatch-c2",
  "stn-basaltina-c3-wall-stn-marble-bookmatch-c3",
  "stn-basaltina-c3-wall-stn-marble-bookmatch-c4",
  "stn-basaltina-c3-wall-stn-taj-mahal-quartzite-c0",
  "stn-basaltina-c3-wall-stn-taj-mahal-quartzite-c1",
  "stn-basaltina-c3-wall-stn-taj-mahal-quartzite-c3",
  "stn-basaltina-c3-wall-stn-taj-mahal-quartzite-c4",
  "stn-basaltina-c3-wall-stn-travertine-crazy-c0",
  "stn-basaltina-c3-wall-stn-travertine-crazy-c1",
  "stn-basaltina-c3-wall-stn-travertine-crazy-c2",
  "stn-basaltina-c3-wall-stn-travertine-crazy-c3",
  "stn-basaltina-c3-wall-stn-travertine-crazy-c4",
  "stn-basaltina-c3-wall-tle-botticino-c0",
  "stn-basaltina-c3-wall-tle-botticino-c1",
  "stn-basaltina-c3-wall-tle-botticino-c2",
  "stn-basaltina-c3-wall-tle-botticino-c3",
  "stn-basaltina-c3-wall-tle-botticino-c4",
  "stn-basaltina-c3-wall-tle-calacatta-slab-c0",
  "stn-basaltina-c3-wall-tle-calacatta-slab-c1",
  "stn-basaltina-c3-wall-tle-calacatta-slab-c2",
  "stn-basaltina-c3-wall-tle-calacatta-slab-c3",
  "stn-basaltina-c3-wall-tle-calacatta-slab-c4",
  "stn-basaltina-c3-wall-tle-concrete-porcelain-c0",
  "stn-basaltina-c3-wall-tle-concrete-porcelain-c1",
  "stn-basaltina-c3-wall-tle-concrete-porcelain-c2",
  "stn-basaltina-c3-wall-tle-concrete-porcelain-c3",
  "stn-basaltina-c3-wall-tle-concrete-porcelain-c4",
  "stn-basaltina-c3-wall-tle-herringbone-subway-c0",
  "stn-basaltina-c3-wall-tle-herringbone-subway-c1",
  "stn-basaltina-c3-wall-tle-herringbone-subway-c2",
  "stn-basaltina-c3-wall-tle-herringbone-subway-c3",
  "stn-basaltina-c3-wall-tle-herringbone-subway-c4",
  "stn-basaltina-c3-wall-tle-terrazzo-c0",
  "stn-basaltina-c3-wall-tle-terrazzo-c1",
  "stn-basaltina-c3-wall-tle-terrazzo-c2",
  "stn-basaltina-c3-wall-tle-terrazzo-c3",
  "stn-basaltina-c3-wall-tle-terrazzo-c4",
  "stn-basaltina-c3-wall-tle-zellige-c0",
  "stn-basaltina-c3-wall-tle-zellige-c1",
  "stn-basaltina-c3-wall-tle-zellige-c2",
  "stn-basaltina-c3-wall-tle-zellige-c3",
  "stn-basaltina-c3-wall-tle-zellige-c4",
  "stn-basaltina-c4-wall-stn-basaltina-c0",
  "stn-basaltina-c4-wall-stn-basaltina-c1",
  "stn-basaltina-c4-wall-stn-basaltina-c2",
  "stn-basaltina-c4-wall-stn-basaltina-c3",
  "stn-basaltina-c4-wall-stn-basaltina-c4",
  "stn-basaltina-c4-wall-stn-bluestone-c0",
  "stn-basaltina-c4-wall-stn-bluestone-c1",
  "stn-basaltina-c4-wall-stn-bluestone-c2",
  "stn-basaltina-c4-wall-stn-bluestone-c3",
  "stn-basaltina-c4-wall-stn-bluestone-c4",
  "stn-basaltina-c4-wall-stn-honed-limestone-c0",
  "stn-basaltina-c4-wall-stn-honed-limestone-c1",
  "stn-basaltina-c4-wall-stn-honed-limestone-c2",
  "stn-basaltina-c4-wall-stn-honed-limestone-c3",
  "stn-basaltina-c4-wall-stn-honed-limestone-c4",
  "stn-basaltina-c4-wall-stn-marble-bookmatch-c0",
  "stn-basaltina-c4-wall-stn-marble-bookmatch-c1",
  "stn-basaltina-c4-wall-stn-marble-bookmatch-c2",
  "stn-basaltina-c4-wall-stn-marble-bookmatch-c3",
  "stn-basaltina-c4-wall-stn-marble-bookmatch-c4",
  "stn-basaltina-c4-wall-stn-taj-mahal-quartzite-c0",
  "stn-basaltina-c4-wall-stn-taj-mahal-quartzite-c1",
  "stn-basaltina-c4-wall-stn-taj-mahal-quartzite-c2",
  "stn-basaltina-c4-wall-stn-taj-mahal-quartzite-c3",
  "stn-basaltina-c4-wall-stn-taj-mahal-quartzite-c4",
  "stn-basaltina-c4-wall-stn-travertine-crazy-c0",
  "stn-basaltina-c4-wall-stn-travertine-crazy-c1",
  "stn-basaltina-c4-wall-stn-travertine-crazy-c2",
  "stn-basaltina-c4-wall-stn-travertine-crazy-c3",
  "stn-basaltina-c4-wall-stn-travertine-crazy-c4",
  "stn-basaltina-c4-wall-tle-botticino-c0",
  "stn-basaltina-c4-wall-tle-botticino-c1",
  "stn-basaltina-c4-wall-tle-botticino-c2",
  "stn-basaltina-c4-wall-tle-botticino-c3",
  "stn-basaltina-c4-wall-tle-botticino-c4",
  "stn-basaltina-c4-wall-tle-calacatta-slab-c0",
  "stn-basaltina-c4-wall-tle-calacatta-slab-c1",
  "stn-basaltina-c4-wall-tle-calacatta-slab-c2",
  "stn-basaltina-c4-wall-tle-calacatta-slab-c3",
  "stn-basaltina-c4-wall-tle-calacatta-slab-c4",
  "stn-basaltina-c4-wall-tle-concrete-porcelain-c0",
  "stn-basaltina-c4-wall-tle-concrete-porcelain-c1",
  "stn-basaltina-c4-wall-tle-concrete-porcelain-c2",
  "stn-basaltina-c4-wall-tle-concrete-porcelain-c3",
  "stn-basaltina-c4-wall-tle-concrete-porcelain-c4",
  "stn-basaltina-c4-wall-tle-herringbone-subway-c0",
  "stn-basaltina-c4-wall-tle-herringbone-subway-c1",
  "stn-basaltina-c4-wall-tle-herringbone-subway-c2",
  "stn-basaltina-c4-wall-tle-herringbone-subway-c3",
  "stn-basaltina-c4-wall-tle-herringbone-subway-c4",
  "stn-basaltina-c4-wall-tle-terrazzo-c0",
  "stn-basaltina-c4-wall-tle-terrazzo-c1",
  "stn-basaltina-c4-wall-tle-terrazzo-c2",
  "stn-basaltina-c4-wall-tle-terrazzo-c3",
  "stn-basaltina-c4-wall-tle-terrazzo-c4",
  "stn-basaltina-c4-wall-tle-zellige-c0",
  "stn-basaltina-c4-wall-tle-zellige-c1",
  "stn-basaltina-c4-wall-tle-zellige-c2",
  "stn-basaltina-c4-wall-tle-zellige-c3",
  "stn-basaltina-c4-wall-tle-zellige-c4",
  "stn-bluestone-c0-wall-stn-bluestone-c1",
  "stn-bluestone-c0-wall-stn-bluestone-c2",
  "stn-bluestone-c0-wall-stn-bluestone-c3",
  "stn-bluestone-c0-wall-stn-bluestone-c4",
  "stn-bluestone-c0-wall-stn-taj-mahal-quartzite-c1",
  "stn-bluestone-c0-wall-stn-taj-mahal-quartzite-c2",
  "stn-bluestone-c0-wall-stn-taj-mahal-quartzite-c3",
  "stn-bluestone-c0-wall-stn-taj-mahal-quartzite-c4",
  "stn-bluestone-c0-wall-tle-botticino-c1",
  "stn-bluestone-c0-wall-tle-botticino-c2",
  "stn-bluestone-c0-wall-tle-botticino-c3",
  "stn-bluestone-c0-wall-tle-botticino-c4",
  "stn-bluestone-c0-wall-tle-calacatta-slab-c1",
  "stn-bluestone-c0-wall-tle-calacatta-slab-c2",
  "stn-bluestone-c0-wall-tle-calacatta-slab-c3",
  "stn-bluestone-c0-wall-tle-calacatta-slab-c4",
  "stn-bluestone-c0-wall-tle-concrete-porcelain-c1",
  "stn-bluestone-c0-wall-tle-concrete-porcelain-c2",
  "stn-bluestone-c0-wall-tle-concrete-porcelain-c3",
  "stn-bluestone-c0-wall-tle-concrete-porcelain-c4",
  "stn-bluestone-c0-wall-tle-herringbone-subway-c1",
  "stn-bluestone-c0-wall-tle-herringbone-subway-c2",
  "stn-bluestone-c0-wall-tle-herringbone-subway-c3",
  "stn-bluestone-c0-wall-tle-herringbone-subway-c4",
  "stn-bluestone-c0-wall-tle-terrazzo-c1",
  "stn-bluestone-c0-wall-tle-terrazzo-c2",
  "stn-bluestone-c0-wall-tle-terrazzo-c3",
  "stn-bluestone-c0-wall-tle-terrazzo-c4",
  "stn-bluestone-c0-wall-tle-zellige-c1",
  "stn-bluestone-c0-wall-tle-zellige-c2",
  "stn-bluestone-c0-wall-tle-zellige-c3",
  "stn-bluestone-c0-wall-tle-zellige-c4",
  "stn-bluestone-c1-wall-stn-bluestone-c0",
  "stn-bluestone-c1-wall-stn-bluestone-c1",
  "stn-bluestone-c1-wall-stn-bluestone-c2",
  "stn-bluestone-c1-wall-stn-bluestone-c3",
  "stn-bluestone-c1-wall-stn-bluestone-c4",
  "stn-bluestone-c1-wall-stn-taj-mahal-quartzite-c0",
  "stn-bluestone-c1-wall-stn-taj-mahal-quartzite-c1",
  "stn-bluestone-c1-wall-stn-taj-mahal-quartzite-c2",
  "stn-bluestone-c1-wall-stn-taj-mahal-quartzite-c3",
  "stn-bluestone-c1-wall-tle-botticino-c0",
  "stn-bluestone-c1-wall-tle-botticino-c1",
  "stn-bluestone-c1-wall-tle-botticino-c2",
  "stn-bluestone-c1-wall-tle-botticino-c3",
  "stn-bluestone-c1-wall-tle-botticino-c4",
  "stn-bluestone-c1-wall-tle-calacatta-slab-c0",
  "stn-bluestone-c1-wall-tle-calacatta-slab-c1",
  "stn-bluestone-c1-wall-tle-calacatta-slab-c2",
  "stn-bluestone-c1-wall-tle-calacatta-slab-c3",
  "stn-bluestone-c1-wall-tle-calacatta-slab-c4",
  "stn-bluestone-c1-wall-tle-concrete-porcelain-c0",
  "stn-bluestone-c1-wall-tle-concrete-porcelain-c1",
  "stn-bluestone-c1-wall-tle-concrete-porcelain-c2",
  "stn-bluestone-c1-wall-tle-concrete-porcelain-c3",
  "stn-bluestone-c1-wall-tle-concrete-porcelain-c4",
  "stn-bluestone-c1-wall-tle-herringbone-subway-c0",
  "stn-bluestone-c1-wall-tle-herringbone-subway-c1",
  "stn-bluestone-c1-wall-tle-herringbone-subway-c2",
  "stn-bluestone-c1-wall-tle-herringbone-subway-c3",
  "stn-bluestone-c1-wall-tle-herringbone-subway-c4",
  "stn-bluestone-c1-wall-tle-terrazzo-c0",
  "stn-bluestone-c1-wall-tle-terrazzo-c1",
  "stn-bluestone-c1-wall-tle-terrazzo-c2",
  "stn-bluestone-c1-wall-tle-terrazzo-c3",
  "stn-bluestone-c1-wall-tle-terrazzo-c4",
  "stn-bluestone-c1-wall-tle-zellige-c0",
  "stn-bluestone-c1-wall-tle-zellige-c1",
  "stn-bluestone-c1-wall-tle-zellige-c2",
  "stn-bluestone-c1-wall-tle-zellige-c3",
  "stn-bluestone-c1-wall-tle-zellige-c4",
  "stn-bluestone-c2-wall-stn-bluestone-c0",
  "stn-bluestone-c2-wall-stn-bluestone-c1",
  "stn-bluestone-c2-wall-stn-bluestone-c2",
  "stn-bluestone-c2-wall-stn-bluestone-c3",
  "stn-bluestone-c2-wall-stn-bluestone-c4",
  "stn-bluestone-c2-wall-tle-botticino-c0",
  "stn-bluestone-c2-wall-tle-botticino-c1",
  "stn-bluestone-c2-wall-tle-botticino-c2",
  "stn-bluestone-c2-wall-tle-botticino-c3",
  "stn-bluestone-c2-wall-tle-botticino-c4",
  "stn-bluestone-c2-wall-tle-calacatta-slab-c0",
  "stn-bluestone-c2-wall-tle-calacatta-slab-c1",
  "stn-bluestone-c2-wall-tle-calacatta-slab-c2",
  "stn-bluestone-c2-wall-tle-calacatta-slab-c3",
  "stn-bluestone-c2-wall-tle-calacatta-slab-c4",
  "stn-bluestone-c2-wall-tle-concrete-porcelain-c0",
  "stn-bluestone-c2-wall-tle-concrete-porcelain-c1",
  "stn-bluestone-c2-wall-tle-concrete-porcelain-c2",
  "stn-bluestone-c2-wall-tle-concrete-porcelain-c3",
  "stn-bluestone-c2-wall-tle-concrete-porcelain-c4",
  "stn-bluestone-c2-wall-tle-herringbone-subway-c0",
  "stn-bluestone-c2-wall-tle-herringbone-subway-c1",
  "stn-bluestone-c2-wall-tle-herringbone-subway-c2",
  "stn-bluestone-c2-wall-tle-herringbone-subway-c3",
  "stn-bluestone-c2-wall-tle-herringbone-subway-c4",
  "stn-bluestone-c2-wall-tle-terrazzo-c0",
  "stn-bluestone-c2-wall-tle-terrazzo-c1",
  "stn-bluestone-c2-wall-tle-terrazzo-c2",
  "stn-bluestone-c2-wall-tle-terrazzo-c3",
  "stn-bluestone-c2-wall-tle-terrazzo-c4",
  "stn-bluestone-c2-wall-tle-zellige-c0",
  "stn-bluestone-c2-wall-tle-zellige-c1",
  "stn-bluestone-c2-wall-tle-zellige-c2",
  "stn-bluestone-c2-wall-tle-zellige-c3",
  "stn-bluestone-c2-wall-tle-zellige-c4",
  "stn-bluestone-c3-wall-stn-bluestone-c0",
  "stn-bluestone-c3-wall-stn-bluestone-c1",
  "stn-bluestone-c3-wall-stn-bluestone-c2",
  "stn-bluestone-c3-wall-stn-bluestone-c3",
  "stn-bluestone-c3-wall-stn-bluestone-c4",
  "stn-bluestone-c3-wall-tle-botticino-c0",
  "stn-bluestone-c3-wall-tle-botticino-c1",
  "stn-bluestone-c3-wall-tle-botticino-c2",
  "stn-bluestone-c3-wall-tle-botticino-c3",
  "stn-bluestone-c3-wall-tle-botticino-c4",
  "stn-bluestone-c3-wall-tle-calacatta-slab-c0",
  "stn-bluestone-c3-wall-tle-calacatta-slab-c1",
  "stn-bluestone-c3-wall-tle-calacatta-slab-c2",
  "stn-bluestone-c3-wall-tle-calacatta-slab-c3",
  "stn-bluestone-c3-wall-tle-calacatta-slab-c4",
  "stn-bluestone-c3-wall-tle-concrete-porcelain-c0",
  "stn-bluestone-c3-wall-tle-concrete-porcelain-c1",
  "stn-bluestone-c3-wall-tle-concrete-porcelain-c2",
  "stn-bluestone-c3-wall-tle-concrete-porcelain-c3",
  "stn-bluestone-c3-wall-tle-concrete-porcelain-c4",
  "stn-bluestone-c3-wall-tle-herringbone-subway-c0",
  "stn-bluestone-c3-wall-tle-herringbone-subway-c1",
  "stn-bluestone-c3-wall-tle-herringbone-subway-c2",
  "stn-bluestone-c3-wall-tle-herringbone-subway-c3",
  "stn-bluestone-c3-wall-tle-herringbone-subway-c4",
  "stn-bluestone-c3-wall-tle-terrazzo-c0",
  "stn-bluestone-c3-wall-tle-terrazzo-c1",
  "stn-bluestone-c3-wall-tle-terrazzo-c2",
  "stn-bluestone-c3-wall-tle-terrazzo-c3",
  "stn-bluestone-c3-wall-tle-terrazzo-c4",
  "stn-bluestone-c3-wall-tle-zellige-c0",
  "stn-bluestone-c3-wall-tle-zellige-c1",
  "stn-bluestone-c3-wall-tle-zellige-c2",
  "stn-bluestone-c3-wall-tle-zellige-c3",
  "stn-bluestone-c3-wall-tle-zellige-c4",
  "stn-bluestone-c4-wall-stn-bluestone-c0",
  "stn-bluestone-c4-wall-stn-bluestone-c1",
  "stn-bluestone-c4-wall-stn-bluestone-c2",
  "stn-bluestone-c4-wall-stn-bluestone-c3",
  "stn-bluestone-c4-wall-stn-bluestone-c4",
  "stn-bluestone-c4-wall-tle-botticino-c0",
  "stn-bluestone-c4-wall-tle-botticino-c1",
  "stn-bluestone-c4-wall-tle-botticino-c2",
  "stn-bluestone-c4-wall-tle-botticino-c3",
  "stn-bluestone-c4-wall-tle-botticino-c4",
  "stn-bluestone-c4-wall-tle-calacatta-slab-c0",
  "stn-bluestone-c4-wall-tle-calacatta-slab-c1",
  "stn-bluestone-c4-wall-tle-calacatta-slab-c2",
  "stn-bluestone-c4-wall-tle-calacatta-slab-c3",
  "stn-bluestone-c4-wall-tle-calacatta-slab-c4",
  "stn-bluestone-c4-wall-tle-concrete-porcelain-c0",
  "stn-bluestone-c4-wall-tle-concrete-porcelain-c1",
  "stn-bluestone-c4-wall-tle-concrete-porcelain-c2",
  "stn-bluestone-c4-wall-tle-concrete-porcelain-c3",
  "stn-bluestone-c4-wall-tle-concrete-porcelain-c4",
  "stn-bluestone-c4-wall-tle-herringbone-subway-c0",
  "stn-bluestone-c4-wall-tle-herringbone-subway-c1",
  "stn-bluestone-c4-wall-tle-herringbone-subway-c2",
  "stn-bluestone-c4-wall-tle-herringbone-subway-c3",
  "stn-bluestone-c4-wall-tle-herringbone-subway-c4",
  "stn-bluestone-c4-wall-tle-terrazzo-c0",
  "stn-bluestone-c4-wall-tle-terrazzo-c1",
  "stn-bluestone-c4-wall-tle-terrazzo-c2",
  "stn-bluestone-c4-wall-tle-terrazzo-c3",
  "stn-bluestone-c4-wall-tle-terrazzo-c4",
  "stn-bluestone-c4-wall-tle-zellige-c0",
  "stn-bluestone-c4-wall-tle-zellige-c1",
  "stn-bluestone-c4-wall-tle-zellige-c2",
  "stn-bluestone-c4-wall-tle-zellige-c3",
  "stn-bluestone-c4-wall-tle-zellige-c4",
  "stn-honed-limestone-c0-wall-stn-basaltina-c1",
  "stn-honed-limestone-c0-wall-stn-basaltina-c2",
  "stn-honed-limestone-c0-wall-stn-basaltina-c3",
  "stn-honed-limestone-c0-wall-stn-basaltina-c4",
  "stn-honed-limestone-c0-wall-stn-bluestone-c1",
  "stn-honed-limestone-c0-wall-stn-bluestone-c2",
  "stn-honed-limestone-c0-wall-stn-bluestone-c3",
  "stn-honed-limestone-c0-wall-stn-bluestone-c4",
  "stn-honed-limestone-c0-wall-stn-honed-limestone-c1",
  "stn-honed-limestone-c0-wall-stn-honed-limestone-c2",
  "stn-honed-limestone-c0-wall-stn-honed-limestone-c3",
  "stn-honed-limestone-c0-wall-stn-honed-limestone-c4",
  "stn-honed-limestone-c0-wall-stn-marble-bookmatch-c1",
  "stn-honed-limestone-c0-wall-stn-marble-bookmatch-c2",
  "stn-honed-limestone-c0-wall-stn-marble-bookmatch-c3",
  "stn-honed-limestone-c0-wall-stn-marble-bookmatch-c4",
  "stn-honed-limestone-c0-wall-stn-taj-mahal-quartzite-c1",
  "stn-honed-limestone-c0-wall-stn-taj-mahal-quartzite-c2",
  "stn-honed-limestone-c0-wall-stn-taj-mahal-quartzite-c3",
  "stn-honed-limestone-c0-wall-stn-taj-mahal-quartzite-c4",
  "stn-honed-limestone-c0-wall-stn-travertine-crazy-c1",
  "stn-honed-limestone-c0-wall-stn-travertine-crazy-c2",
  "stn-honed-limestone-c0-wall-stn-travertine-crazy-c3",
  "stn-honed-limestone-c0-wall-stn-travertine-crazy-c4",
  "stn-honed-limestone-c0-wall-tle-botticino-c1",
  "stn-honed-limestone-c0-wall-tle-botticino-c2",
  "stn-honed-limestone-c0-wall-tle-botticino-c3",
  "stn-honed-limestone-c0-wall-tle-botticino-c4",
  "stn-honed-limestone-c0-wall-tle-calacatta-slab-c1",
  "stn-honed-limestone-c0-wall-tle-calacatta-slab-c2",
  "stn-honed-limestone-c0-wall-tle-calacatta-slab-c3",
  "stn-honed-limestone-c0-wall-tle-calacatta-slab-c4",
  "stn-honed-limestone-c0-wall-tle-concrete-porcelain-c1",
  "stn-honed-limestone-c0-wall-tle-concrete-porcelain-c2",
  "stn-honed-limestone-c0-wall-tle-concrete-porcelain-c3",
  "stn-honed-limestone-c0-wall-tle-concrete-porcelain-c4",
  "stn-honed-limestone-c0-wall-tle-herringbone-subway-c1",
  "stn-honed-limestone-c0-wall-tle-herringbone-subway-c2",
  "stn-honed-limestone-c0-wall-tle-herringbone-subway-c3",
  "stn-honed-limestone-c0-wall-tle-herringbone-subway-c4",
  "stn-honed-limestone-c0-wall-tle-terrazzo-c1",
  "stn-honed-limestone-c0-wall-tle-terrazzo-c2",
  "stn-honed-limestone-c0-wall-tle-terrazzo-c3",
  "stn-honed-limestone-c0-wall-tle-terrazzo-c4",
  "stn-honed-limestone-c0-wall-tle-zellige-c1",
  "stn-honed-limestone-c0-wall-tle-zellige-c2",
  "stn-honed-limestone-c0-wall-tle-zellige-c3",
  "stn-honed-limestone-c0-wall-tle-zellige-c4",
  "stn-honed-limestone-c1-wall-stn-basaltina-c0",
  "stn-honed-limestone-c1-wall-stn-basaltina-c1",
  "stn-honed-limestone-c1-wall-stn-basaltina-c2",
  "stn-honed-limestone-c1-wall-stn-basaltina-c3",
  "stn-honed-limestone-c1-wall-stn-basaltina-c4",
  "stn-honed-limestone-c1-wall-stn-bluestone-c0",
  "stn-honed-limestone-c1-wall-stn-bluestone-c1",
  "stn-honed-limestone-c1-wall-stn-bluestone-c2",
  "stn-honed-limestone-c1-wall-stn-bluestone-c3",
  "stn-honed-limestone-c1-wall-stn-bluestone-c4",
  "stn-honed-limestone-c1-wall-stn-honed-limestone-c0",
  "stn-honed-limestone-c1-wall-stn-honed-limestone-c1",
  "stn-honed-limestone-c1-wall-stn-honed-limestone-c2",
  "stn-honed-limestone-c1-wall-stn-honed-limestone-c3",
  "stn-honed-limestone-c1-wall-stn-honed-limestone-c4",
  "stn-honed-limestone-c1-wall-stn-marble-bookmatch-c0",
  "stn-honed-limestone-c1-wall-stn-marble-bookmatch-c1",
  "stn-honed-limestone-c1-wall-stn-marble-bookmatch-c2",
  "stn-honed-limestone-c1-wall-stn-marble-bookmatch-c3",
  "stn-honed-limestone-c1-wall-stn-marble-bookmatch-c4",
  "stn-honed-limestone-c1-wall-stn-taj-mahal-quartzite-c0",
  "stn-honed-limestone-c1-wall-stn-taj-mahal-quartzite-c1",
  "stn-honed-limestone-c1-wall-stn-taj-mahal-quartzite-c2",
  "stn-honed-limestone-c1-wall-stn-taj-mahal-quartzite-c3",
  "stn-honed-limestone-c1-wall-stn-taj-mahal-quartzite-c4",
  "stn-honed-limestone-c1-wall-stn-travertine-crazy-c0",
  "stn-honed-limestone-c1-wall-stn-travertine-crazy-c1",
  "stn-honed-limestone-c1-wall-stn-travertine-crazy-c2",
  "stn-honed-limestone-c1-wall-stn-travertine-crazy-c3",
  "stn-honed-limestone-c1-wall-stn-travertine-crazy-c4",
  "stn-honed-limestone-c1-wall-tle-botticino-c0",
  "stn-honed-limestone-c1-wall-tle-botticino-c1",
  "stn-honed-limestone-c1-wall-tle-botticino-c2",
  "stn-honed-limestone-c1-wall-tle-botticino-c3",
  "stn-honed-limestone-c1-wall-tle-botticino-c4",
  "stn-honed-limestone-c1-wall-tle-calacatta-slab-c0",
  "stn-honed-limestone-c1-wall-tle-calacatta-slab-c1",
  "stn-honed-limestone-c1-wall-tle-calacatta-slab-c2",
  "stn-honed-limestone-c1-wall-tle-calacatta-slab-c3",
  "stn-honed-limestone-c1-wall-tle-calacatta-slab-c4",
  "stn-honed-limestone-c1-wall-tle-concrete-porcelain-c0",
  "stn-honed-limestone-c1-wall-tle-concrete-porcelain-c1",
  "stn-honed-limestone-c1-wall-tle-concrete-porcelain-c2",
  "stn-honed-limestone-c1-wall-tle-concrete-porcelain-c3",
  "stn-honed-limestone-c1-wall-tle-concrete-porcelain-c4",
  "stn-honed-limestone-c1-wall-tle-herringbone-subway-c0",
  "stn-honed-limestone-c1-wall-tle-herringbone-subway-c1",
  "stn-honed-limestone-c1-wall-tle-herringbone-subway-c2",
  "stn-honed-limestone-c1-wall-tle-herringbone-subway-c3",
  "stn-honed-limestone-c1-wall-tle-herringbone-subway-c4",
  "stn-honed-limestone-c1-wall-tle-terrazzo-c0",
  "stn-honed-limestone-c1-wall-tle-terrazzo-c1",
  "stn-honed-limestone-c1-wall-tle-terrazzo-c2",
  "stn-honed-limestone-c1-wall-tle-terrazzo-c3",
  "stn-honed-limestone-c1-wall-tle-terrazzo-c4",
  "stn-honed-limestone-c1-wall-tle-zellige-c0",
  "stn-honed-limestone-c1-wall-tle-zellige-c1",
  "stn-honed-limestone-c1-wall-tle-zellige-c2",
  "stn-honed-limestone-c1-wall-tle-zellige-c3",
  "stn-honed-limestone-c1-wall-tle-zellige-c4",
  "stn-honed-limestone-c2-wall-stn-basaltina-c0",
  "stn-honed-limestone-c2-wall-stn-basaltina-c1",
  "stn-honed-limestone-c2-wall-stn-basaltina-c2",
  "stn-honed-limestone-c2-wall-stn-basaltina-c3",
  "stn-honed-limestone-c2-wall-stn-basaltina-c4",
  "stn-honed-limestone-c2-wall-stn-bluestone-c0",
  "stn-honed-limestone-c2-wall-stn-bluestone-c1",
  "stn-honed-limestone-c2-wall-stn-bluestone-c2",
  "stn-honed-limestone-c2-wall-stn-bluestone-c3",
  "stn-honed-limestone-c2-wall-stn-bluestone-c4",
  "stn-honed-limestone-c2-wall-stn-honed-limestone-c0",
  "stn-honed-limestone-c2-wall-stn-honed-limestone-c1",
  "stn-honed-limestone-c2-wall-stn-honed-limestone-c2",
  "stn-honed-limestone-c2-wall-stn-honed-limestone-c3",
  "stn-honed-limestone-c2-wall-stn-honed-limestone-c4",
  "stn-honed-limestone-c2-wall-stn-marble-bookmatch-c0",
  "stn-honed-limestone-c2-wall-stn-marble-bookmatch-c1",
  "stn-honed-limestone-c2-wall-stn-marble-bookmatch-c2",
  "stn-honed-limestone-c2-wall-stn-marble-bookmatch-c3",
  "stn-honed-limestone-c2-wall-stn-marble-bookmatch-c4",
  "stn-honed-limestone-c2-wall-stn-taj-mahal-quartzite-c0",
  "stn-honed-limestone-c2-wall-stn-taj-mahal-quartzite-c1",
  "stn-honed-limestone-c2-wall-stn-taj-mahal-quartzite-c2",
  "stn-honed-limestone-c2-wall-stn-taj-mahal-quartzite-c3",
  "stn-honed-limestone-c2-wall-stn-taj-mahal-quartzite-c4",
  "stn-honed-limestone-c2-wall-stn-travertine-crazy-c0",
  "stn-honed-limestone-c2-wall-stn-travertine-crazy-c1",
  "stn-honed-limestone-c2-wall-stn-travertine-crazy-c2",
  "stn-honed-limestone-c2-wall-stn-travertine-crazy-c3",
  "stn-honed-limestone-c2-wall-stn-travertine-crazy-c4",
  "stn-honed-limestone-c2-wall-tle-botticino-c0",
  "stn-honed-limestone-c2-wall-tle-botticino-c1",
  "stn-honed-limestone-c2-wall-tle-botticino-c2",
  "stn-honed-limestone-c2-wall-tle-botticino-c3",
  "stn-honed-limestone-c2-wall-tle-botticino-c4",
  "stn-honed-limestone-c2-wall-tle-calacatta-slab-c0",
  "stn-honed-limestone-c2-wall-tle-calacatta-slab-c1",
  "stn-honed-limestone-c2-wall-tle-calacatta-slab-c2",
  "stn-honed-limestone-c2-wall-tle-calacatta-slab-c3",
  "stn-honed-limestone-c2-wall-tle-calacatta-slab-c4",
  "stn-honed-limestone-c2-wall-tle-concrete-porcelain-c0",
  "stn-honed-limestone-c2-wall-tle-concrete-porcelain-c1",
  "stn-honed-limestone-c2-wall-tle-concrete-porcelain-c2",
  "stn-honed-limestone-c2-wall-tle-concrete-porcelain-c3",
  "stn-honed-limestone-c2-wall-tle-concrete-porcelain-c4",
  "stn-honed-limestone-c2-wall-tle-herringbone-subway-c0",
  "stn-honed-limestone-c2-wall-tle-herringbone-subway-c1",
  "stn-honed-limestone-c2-wall-tle-herringbone-subway-c2",
  "stn-honed-limestone-c2-wall-tle-herringbone-subway-c3",
  "stn-honed-limestone-c2-wall-tle-herringbone-subway-c4",
  "stn-honed-limestone-c2-wall-tle-terrazzo-c0",
  "stn-honed-limestone-c2-wall-tle-terrazzo-c1",
  "stn-honed-limestone-c2-wall-tle-terrazzo-c2",
  "stn-honed-limestone-c2-wall-tle-terrazzo-c3",
  "stn-honed-limestone-c2-wall-tle-terrazzo-c4",
  "stn-honed-limestone-c2-wall-tle-zellige-c0",
  "stn-honed-limestone-c2-wall-tle-zellige-c1",
  "stn-honed-limestone-c2-wall-tle-zellige-c2",
  "stn-honed-limestone-c2-wall-tle-zellige-c3",
  "stn-honed-limestone-c2-wall-tle-zellige-c4",
  "stn-honed-limestone-c3-wall-stn-basaltina-c0",
  "stn-honed-limestone-c3-wall-stn-basaltina-c1",
  "stn-honed-limestone-c3-wall-stn-basaltina-c2",
  "stn-honed-limestone-c3-wall-stn-basaltina-c3",
  "stn-honed-limestone-c3-wall-stn-basaltina-c4",
  "stn-honed-limestone-c3-wall-stn-bluestone-c0",
  "stn-honed-limestone-c3-wall-stn-bluestone-c1",
  "stn-honed-limestone-c3-wall-stn-bluestone-c2",
  "stn-honed-limestone-c3-wall-stn-bluestone-c3",
  "stn-honed-limestone-c3-wall-stn-bluestone-c4",
  "stn-honed-limestone-c3-wall-stn-honed-limestone-c0",
  "stn-honed-limestone-c3-wall-stn-honed-limestone-c1",
  "stn-honed-limestone-c3-wall-stn-honed-limestone-c2",
  "stn-honed-limestone-c3-wall-stn-honed-limestone-c3",
  "stn-honed-limestone-c3-wall-stn-honed-limestone-c4",
  "stn-honed-limestone-c3-wall-stn-marble-bookmatch-c0",
  "stn-honed-limestone-c3-wall-stn-marble-bookmatch-c1",
  "stn-honed-limestone-c3-wall-stn-marble-bookmatch-c2",
  "stn-honed-limestone-c3-wall-stn-marble-bookmatch-c3",
  "stn-honed-limestone-c3-wall-stn-marble-bookmatch-c4",
  "stn-honed-limestone-c3-wall-stn-taj-mahal-quartzite-c0",
  "stn-honed-limestone-c3-wall-stn-taj-mahal-quartzite-c1",
  "stn-honed-limestone-c3-wall-stn-taj-mahal-quartzite-c2",
  "stn-honed-limestone-c3-wall-stn-taj-mahal-quartzite-c3",
  "stn-honed-limestone-c3-wall-stn-taj-mahal-quartzite-c4",
  "stn-honed-limestone-c3-wall-stn-travertine-crazy-c0",
  "stn-honed-limestone-c3-wall-stn-travertine-crazy-c1",
  "stn-honed-limestone-c3-wall-stn-travertine-crazy-c2",
  "stn-honed-limestone-c3-wall-stn-travertine-crazy-c3",
  "stn-honed-limestone-c3-wall-stn-travertine-crazy-c4",
  "stn-honed-limestone-c3-wall-tle-botticino-c0",
  "stn-honed-limestone-c3-wall-tle-botticino-c1",
  "stn-honed-limestone-c3-wall-tle-botticino-c2",
  "stn-honed-limestone-c3-wall-tle-botticino-c3",
  "stn-honed-limestone-c3-wall-tle-botticino-c4",
  "stn-honed-limestone-c3-wall-tle-calacatta-slab-c0",
  "stn-honed-limestone-c3-wall-tle-calacatta-slab-c1",
  "stn-honed-limestone-c3-wall-tle-calacatta-slab-c2",
  "stn-honed-limestone-c3-wall-tle-calacatta-slab-c3",
  "stn-honed-limestone-c3-wall-tle-calacatta-slab-c4",
  "stn-honed-limestone-c3-wall-tle-concrete-porcelain-c0",
  "stn-honed-limestone-c3-wall-tle-concrete-porcelain-c1",
  "stn-honed-limestone-c3-wall-tle-concrete-porcelain-c2",
  "stn-honed-limestone-c3-wall-tle-concrete-porcelain-c3",
  "stn-honed-limestone-c3-wall-tle-concrete-porcelain-c4",
  "stn-honed-limestone-c3-wall-tle-herringbone-subway-c0",
  "stn-honed-limestone-c3-wall-tle-herringbone-subway-c1",
  "stn-honed-limestone-c3-wall-tle-herringbone-subway-c2",
  "stn-honed-limestone-c3-wall-tle-herringbone-subway-c3",
  "stn-honed-limestone-c3-wall-tle-herringbone-subway-c4",
  "stn-honed-limestone-c3-wall-tle-terrazzo-c0",
  "stn-honed-limestone-c3-wall-tle-terrazzo-c1",
  "stn-honed-limestone-c3-wall-tle-terrazzo-c2",
  "stn-honed-limestone-c3-wall-tle-terrazzo-c3",
  "stn-honed-limestone-c3-wall-tle-terrazzo-c4",
  "stn-honed-limestone-c3-wall-tle-zellige-c0",
  "stn-honed-limestone-c3-wall-tle-zellige-c1",
  "stn-honed-limestone-c3-wall-tle-zellige-c2",
  "stn-honed-limestone-c3-wall-tle-zellige-c3",
  "stn-honed-limestone-c3-wall-tle-zellige-c4",
  "stn-honed-limestone-c4-wall-stn-basaltina-c0",
  "stn-honed-limestone-c4-wall-stn-basaltina-c1",
  "stn-honed-limestone-c4-wall-stn-basaltina-c2",
  "stn-honed-limestone-c4-wall-stn-basaltina-c3",
  "stn-honed-limestone-c4-wall-stn-basaltina-c4",
  "stn-honed-limestone-c4-wall-stn-bluestone-c0",
  "stn-honed-limestone-c4-wall-stn-bluestone-c1",
  "stn-honed-limestone-c4-wall-stn-bluestone-c2",
  "stn-honed-limestone-c4-wall-stn-bluestone-c3",
  "stn-honed-limestone-c4-wall-stn-bluestone-c4",
  "stn-honed-limestone-c4-wall-stn-honed-limestone-c0",
  "stn-honed-limestone-c4-wall-stn-honed-limestone-c1",
  "stn-honed-limestone-c4-wall-stn-honed-limestone-c2",
  "stn-honed-limestone-c4-wall-stn-honed-limestone-c3",
  "stn-honed-limestone-c4-wall-stn-honed-limestone-c4",
  "stn-honed-limestone-c4-wall-stn-marble-bookmatch-c0",
  "stn-honed-limestone-c4-wall-stn-marble-bookmatch-c1",
  "stn-honed-limestone-c4-wall-stn-marble-bookmatch-c2",
  "stn-honed-limestone-c4-wall-stn-marble-bookmatch-c3",
  "stn-honed-limestone-c4-wall-stn-marble-bookmatch-c4",
  "stn-honed-limestone-c4-wall-stn-taj-mahal-quartzite-c0",
  "stn-honed-limestone-c4-wall-stn-taj-mahal-quartzite-c1",
  "stn-honed-limestone-c4-wall-stn-taj-mahal-quartzite-c2",
  "stn-honed-limestone-c4-wall-stn-taj-mahal-quartzite-c3",
  "stn-honed-limestone-c4-wall-stn-taj-mahal-quartzite-c4",
  "stn-honed-limestone-c4-wall-stn-travertine-crazy-c0",
  "stn-honed-limestone-c4-wall-stn-travertine-crazy-c1",
  "stn-honed-limestone-c4-wall-stn-travertine-crazy-c2",
  "stn-honed-limestone-c4-wall-stn-travertine-crazy-c3",
  "stn-honed-limestone-c4-wall-stn-travertine-crazy-c4",
  "stn-honed-limestone-c4-wall-tle-botticino-c0",
  "stn-honed-limestone-c4-wall-tle-botticino-c1",
  "stn-honed-limestone-c4-wall-tle-botticino-c2",
  "stn-honed-limestone-c4-wall-tle-botticino-c3",
  "stn-honed-limestone-c4-wall-tle-botticino-c4",
  "stn-honed-limestone-c4-wall-tle-calacatta-slab-c0",
  "stn-honed-limestone-c4-wall-tle-calacatta-slab-c1",
  "stn-honed-limestone-c4-wall-tle-calacatta-slab-c2",
  "stn-honed-limestone-c4-wall-tle-calacatta-slab-c3",
  "stn-honed-limestone-c4-wall-tle-calacatta-slab-c4",
  "stn-honed-limestone-c4-wall-tle-concrete-porcelain-c0",
  "stn-honed-limestone-c4-wall-tle-concrete-porcelain-c1",
  "stn-honed-limestone-c4-wall-tle-concrete-porcelain-c2",
  "stn-honed-limestone-c4-wall-tle-concrete-porcelain-c3",
  "stn-honed-limestone-c4-wall-tle-concrete-porcelain-c4",
  "stn-honed-limestone-c4-wall-tle-herringbone-subway-c0",
  "stn-honed-limestone-c4-wall-tle-herringbone-subway-c1",
  "stn-honed-limestone-c4-wall-tle-herringbone-subway-c2",
  "stn-honed-limestone-c4-wall-tle-herringbone-subway-c3",
  "stn-honed-limestone-c4-wall-tle-herringbone-subway-c4",
  "stn-honed-limestone-c4-wall-tle-terrazzo-c0",
  "stn-honed-limestone-c4-wall-tle-terrazzo-c1",
  "stn-honed-limestone-c4-wall-tle-terrazzo-c2",
  "stn-honed-limestone-c4-wall-tle-terrazzo-c3",
  "stn-honed-limestone-c4-wall-tle-terrazzo-c4",
  "stn-honed-limestone-c4-wall-tle-zellige-c0",
  "stn-honed-limestone-c4-wall-tle-zellige-c1",
  "stn-honed-limestone-c4-wall-tle-zellige-c2",
  "stn-honed-limestone-c4-wall-tle-zellige-c3",
  "stn-honed-limestone-c4-wall-tle-zellige-c4",
  "stn-marble-bookmatch-c0-wall-stn-basaltina-c1",
  "stn-marble-bookmatch-c0-wall-stn-basaltina-c2",
  "stn-marble-bookmatch-c0-wall-stn-basaltina-c3",
  "stn-marble-bookmatch-c0-wall-stn-basaltina-c4",
  "stn-marble-bookmatch-c0-wall-stn-bluestone-c1",
  "stn-marble-bookmatch-c0-wall-stn-bluestone-c2",
  "stn-marble-bookmatch-c0-wall-stn-bluestone-c3",
  "stn-marble-bookmatch-c0-wall-stn-bluestone-c4",
  "stn-marble-bookmatch-c0-wall-stn-honed-limestone-c1",
  "stn-marble-bookmatch-c0-wall-stn-honed-limestone-c2",
  "stn-marble-bookmatch-c0-wall-stn-honed-limestone-c3",
  "stn-marble-bookmatch-c0-wall-stn-honed-limestone-c4",
  "stn-marble-bookmatch-c0-wall-stn-marble-bookmatch-c1",
  "stn-marble-bookmatch-c0-wall-stn-marble-bookmatch-c2",
  "stn-marble-bookmatch-c0-wall-stn-marble-bookmatch-c3",
  "stn-marble-bookmatch-c0-wall-stn-marble-bookmatch-c4",
  "stn-marble-bookmatch-c0-wall-stn-taj-mahal-quartzite-c1",
  "stn-marble-bookmatch-c0-wall-stn-taj-mahal-quartzite-c2",
  "stn-marble-bookmatch-c0-wall-stn-taj-mahal-quartzite-c3",
  "stn-marble-bookmatch-c0-wall-stn-taj-mahal-quartzite-c4",
  "stn-marble-bookmatch-c0-wall-stn-travertine-crazy-c1",
  "stn-marble-bookmatch-c0-wall-stn-travertine-crazy-c2",
  "stn-marble-bookmatch-c0-wall-stn-travertine-crazy-c3",
  "stn-marble-bookmatch-c0-wall-stn-travertine-crazy-c4",
  "stn-marble-bookmatch-c0-wall-tle-botticino-c1",
  "stn-marble-bookmatch-c0-wall-tle-botticino-c2",
  "stn-marble-bookmatch-c0-wall-tle-botticino-c3",
  "stn-marble-bookmatch-c0-wall-tle-botticino-c4",
  "stn-marble-bookmatch-c0-wall-tle-calacatta-slab-c1",
  "stn-marble-bookmatch-c0-wall-tle-calacatta-slab-c2",
  "stn-marble-bookmatch-c0-wall-tle-calacatta-slab-c3",
  "stn-marble-bookmatch-c0-wall-tle-calacatta-slab-c4",
  "stn-marble-bookmatch-c0-wall-tle-concrete-porcelain-c1",
  "stn-marble-bookmatch-c0-wall-tle-concrete-porcelain-c2",
  "stn-marble-bookmatch-c0-wall-tle-concrete-porcelain-c3",
  "stn-marble-bookmatch-c0-wall-tle-concrete-porcelain-c4",
  "stn-marble-bookmatch-c0-wall-tle-herringbone-subway-c1",
  "stn-marble-bookmatch-c0-wall-tle-herringbone-subway-c2",
  "stn-marble-bookmatch-c0-wall-tle-herringbone-subway-c3",
  "stn-marble-bookmatch-c0-wall-tle-herringbone-subway-c4",
  "stn-marble-bookmatch-c0-wall-tle-terrazzo-c1",
  "stn-marble-bookmatch-c0-wall-tle-terrazzo-c2",
  "stn-marble-bookmatch-c0-wall-tle-terrazzo-c3",
  "stn-marble-bookmatch-c0-wall-tle-terrazzo-c4",
  "stn-marble-bookmatch-c0-wall-tle-zellige-c1",
  "stn-marble-bookmatch-c0-wall-tle-zellige-c2",
  "stn-marble-bookmatch-c0-wall-tle-zellige-c3",
  "stn-marble-bookmatch-c0-wall-tle-zellige-c4",
  "stn-marble-bookmatch-c1-wall-stn-basaltina-c0",
  "stn-marble-bookmatch-c1-wall-stn-basaltina-c1",
  "stn-marble-bookmatch-c1-wall-stn-basaltina-c2",
  "stn-marble-bookmatch-c1-wall-stn-basaltina-c3",
  "stn-marble-bookmatch-c1-wall-stn-basaltina-c4",
  "stn-marble-bookmatch-c1-wall-stn-bluestone-c0",
  "stn-marble-bookmatch-c1-wall-stn-bluestone-c1",
  "stn-marble-bookmatch-c1-wall-stn-bluestone-c2",
  "stn-marble-bookmatch-c1-wall-stn-bluestone-c3",
  "stn-marble-bookmatch-c1-wall-stn-bluestone-c4",
  "stn-marble-bookmatch-c1-wall-stn-honed-limestone-c0",
  "stn-marble-bookmatch-c1-wall-stn-honed-limestone-c1",
  "stn-marble-bookmatch-c1-wall-stn-honed-limestone-c2",
  "stn-marble-bookmatch-c1-wall-stn-honed-limestone-c3",
  "stn-marble-bookmatch-c1-wall-stn-honed-limestone-c4",
  "stn-marble-bookmatch-c1-wall-stn-marble-bookmatch-c0",
  "stn-marble-bookmatch-c1-wall-stn-marble-bookmatch-c1",
  "stn-marble-bookmatch-c1-wall-stn-marble-bookmatch-c2",
  "stn-marble-bookmatch-c1-wall-stn-marble-bookmatch-c3",
  "stn-marble-bookmatch-c1-wall-stn-marble-bookmatch-c4",
  "stn-marble-bookmatch-c1-wall-stn-taj-mahal-quartzite-c0",
  "stn-marble-bookmatch-c1-wall-stn-taj-mahal-quartzite-c1",
  "stn-marble-bookmatch-c1-wall-stn-taj-mahal-quartzite-c2",
  "stn-marble-bookmatch-c1-wall-stn-taj-mahal-quartzite-c3",
  "stn-marble-bookmatch-c1-wall-stn-taj-mahal-quartzite-c4",
  "stn-marble-bookmatch-c1-wall-stn-travertine-crazy-c0",
  "stn-marble-bookmatch-c1-wall-stn-travertine-crazy-c1",
  "stn-marble-bookmatch-c1-wall-stn-travertine-crazy-c2",
  "stn-marble-bookmatch-c1-wall-stn-travertine-crazy-c3",
  "stn-marble-bookmatch-c1-wall-stn-travertine-crazy-c4",
  "stn-marble-bookmatch-c1-wall-tle-botticino-c0",
  "stn-marble-bookmatch-c1-wall-tle-botticino-c1",
  "stn-marble-bookmatch-c1-wall-tle-botticino-c2",
  "stn-marble-bookmatch-c1-wall-tle-botticino-c3",
  "stn-marble-bookmatch-c1-wall-tle-botticino-c4",
  "stn-marble-bookmatch-c1-wall-tle-calacatta-slab-c0",
  "stn-marble-bookmatch-c1-wall-tle-calacatta-slab-c1",
  "stn-marble-bookmatch-c1-wall-tle-calacatta-slab-c2",
  "stn-marble-bookmatch-c1-wall-tle-calacatta-slab-c3",
  "stn-marble-bookmatch-c1-wall-tle-calacatta-slab-c4",
  "stn-marble-bookmatch-c1-wall-tle-concrete-porcelain-c0",
  "stn-marble-bookmatch-c1-wall-tle-concrete-porcelain-c1",
  "stn-marble-bookmatch-c1-wall-tle-concrete-porcelain-c2",
  "stn-marble-bookmatch-c1-wall-tle-concrete-porcelain-c3",
  "stn-marble-bookmatch-c1-wall-tle-concrete-porcelain-c4",
  "stn-marble-bookmatch-c1-wall-tle-herringbone-subway-c0",
  "stn-marble-bookmatch-c1-wall-tle-herringbone-subway-c1",
  "stn-marble-bookmatch-c1-wall-tle-herringbone-subway-c2",
  "stn-marble-bookmatch-c1-wall-tle-herringbone-subway-c3",
  "stn-marble-bookmatch-c1-wall-tle-herringbone-subway-c4",
  "stn-marble-bookmatch-c1-wall-tle-terrazzo-c0",
  "stn-marble-bookmatch-c1-wall-tle-terrazzo-c1",
  "stn-marble-bookmatch-c1-wall-tle-terrazzo-c2",
  "stn-marble-bookmatch-c1-wall-tle-terrazzo-c3",
  "stn-marble-bookmatch-c1-wall-tle-terrazzo-c4",
  "stn-marble-bookmatch-c1-wall-tle-zellige-c0",
  "stn-marble-bookmatch-c1-wall-tle-zellige-c1",
  "stn-marble-bookmatch-c1-wall-tle-zellige-c2",
  "stn-marble-bookmatch-c1-wall-tle-zellige-c3",
  "stn-marble-bookmatch-c1-wall-tle-zellige-c4",
  "stn-marble-bookmatch-c2-wall-stn-basaltina-c0",
  "stn-marble-bookmatch-c2-wall-stn-basaltina-c1",
  "stn-marble-bookmatch-c2-wall-stn-basaltina-c2",
  "stn-marble-bookmatch-c2-wall-stn-basaltina-c3",
  "stn-marble-bookmatch-c2-wall-stn-basaltina-c4",
  "stn-marble-bookmatch-c2-wall-stn-bluestone-c0",
  "stn-marble-bookmatch-c2-wall-stn-bluestone-c1",
  "stn-marble-bookmatch-c2-wall-stn-bluestone-c2",
  "stn-marble-bookmatch-c2-wall-stn-bluestone-c3",
  "stn-marble-bookmatch-c2-wall-stn-bluestone-c4",
  "stn-marble-bookmatch-c2-wall-stn-honed-limestone-c0",
  "stn-marble-bookmatch-c2-wall-stn-honed-limestone-c1",
  "stn-marble-bookmatch-c2-wall-stn-honed-limestone-c2",
  "stn-marble-bookmatch-c2-wall-stn-honed-limestone-c3",
  "stn-marble-bookmatch-c2-wall-stn-honed-limestone-c4",
  "stn-marble-bookmatch-c2-wall-stn-marble-bookmatch-c0",
  "stn-marble-bookmatch-c2-wall-stn-marble-bookmatch-c1",
  "stn-marble-bookmatch-c2-wall-stn-marble-bookmatch-c2",
  "stn-marble-bookmatch-c2-wall-stn-marble-bookmatch-c3",
  "stn-marble-bookmatch-c2-wall-stn-marble-bookmatch-c4",
  "stn-marble-bookmatch-c2-wall-stn-taj-mahal-quartzite-c0",
  "stn-marble-bookmatch-c2-wall-stn-taj-mahal-quartzite-c1",
  "stn-marble-bookmatch-c2-wall-stn-taj-mahal-quartzite-c2",
  "stn-marble-bookmatch-c2-wall-stn-taj-mahal-quartzite-c3",
  "stn-marble-bookmatch-c2-wall-stn-taj-mahal-quartzite-c4",
  "stn-marble-bookmatch-c2-wall-stn-travertine-crazy-c0",
  "stn-marble-bookmatch-c2-wall-stn-travertine-crazy-c1",
  "stn-marble-bookmatch-c2-wall-stn-travertine-crazy-c2",
  "stn-marble-bookmatch-c2-wall-stn-travertine-crazy-c3",
  "stn-marble-bookmatch-c2-wall-stn-travertine-crazy-c4",
  "stn-marble-bookmatch-c2-wall-tle-botticino-c0",
  "stn-marble-bookmatch-c2-wall-tle-botticino-c1",
  "stn-marble-bookmatch-c2-wall-tle-botticino-c2",
  "stn-marble-bookmatch-c2-wall-tle-botticino-c3",
  "stn-marble-bookmatch-c2-wall-tle-botticino-c4",
  "stn-marble-bookmatch-c2-wall-tle-calacatta-slab-c0",
  "stn-marble-bookmatch-c2-wall-tle-calacatta-slab-c1",
  "stn-marble-bookmatch-c2-wall-tle-calacatta-slab-c2",
  "stn-marble-bookmatch-c2-wall-tle-calacatta-slab-c3",
  "stn-marble-bookmatch-c2-wall-tle-calacatta-slab-c4",
  "stn-marble-bookmatch-c2-wall-tle-concrete-porcelain-c0",
  "stn-marble-bookmatch-c2-wall-tle-concrete-porcelain-c1",
  "stn-marble-bookmatch-c2-wall-tle-concrete-porcelain-c2",
  "stn-marble-bookmatch-c2-wall-tle-concrete-porcelain-c3",
  "stn-marble-bookmatch-c2-wall-tle-concrete-porcelain-c4",
  "stn-marble-bookmatch-c2-wall-tle-herringbone-subway-c0",
  "stn-marble-bookmatch-c2-wall-tle-herringbone-subway-c1",
  "stn-marble-bookmatch-c2-wall-tle-herringbone-subway-c2",
  "stn-marble-bookmatch-c2-wall-tle-herringbone-subway-c3",
  "stn-marble-bookmatch-c2-wall-tle-herringbone-subway-c4",
  "stn-marble-bookmatch-c2-wall-tle-terrazzo-c0",
  "stn-marble-bookmatch-c2-wall-tle-terrazzo-c1",
  "stn-marble-bookmatch-c2-wall-tle-terrazzo-c2",
  "stn-marble-bookmatch-c2-wall-tle-terrazzo-c3",
  "stn-marble-bookmatch-c2-wall-tle-terrazzo-c4",
  "stn-marble-bookmatch-c2-wall-tle-zellige-c0",
  "stn-marble-bookmatch-c2-wall-tle-zellige-c1",
  "stn-marble-bookmatch-c2-wall-tle-zellige-c2",
  "stn-marble-bookmatch-c2-wall-tle-zellige-c3",
  "stn-marble-bookmatch-c2-wall-tle-zellige-c4",
  "stn-marble-bookmatch-c3-wall-stn-basaltina-c0",
  "stn-marble-bookmatch-c3-wall-stn-basaltina-c1",
  "stn-marble-bookmatch-c3-wall-stn-basaltina-c2",
  "stn-marble-bookmatch-c3-wall-stn-basaltina-c3",
  "stn-marble-bookmatch-c3-wall-stn-basaltina-c4",
  "stn-marble-bookmatch-c3-wall-stn-bluestone-c0",
  "stn-marble-bookmatch-c3-wall-stn-bluestone-c1",
  "stn-marble-bookmatch-c3-wall-stn-bluestone-c2",
  "stn-marble-bookmatch-c3-wall-stn-bluestone-c3",
  "stn-marble-bookmatch-c3-wall-stn-bluestone-c4",
  "stn-marble-bookmatch-c3-wall-stn-honed-limestone-c0",
  "stn-marble-bookmatch-c3-wall-stn-honed-limestone-c1",
  "stn-marble-bookmatch-c3-wall-stn-honed-limestone-c2",
  "stn-marble-bookmatch-c3-wall-stn-honed-limestone-c3",
  "stn-marble-bookmatch-c3-wall-stn-honed-limestone-c4",
  "stn-marble-bookmatch-c3-wall-stn-marble-bookmatch-c0",
  "stn-marble-bookmatch-c3-wall-stn-marble-bookmatch-c1",
  "stn-marble-bookmatch-c3-wall-stn-marble-bookmatch-c2",
  "stn-marble-bookmatch-c3-wall-stn-marble-bookmatch-c3",
  "stn-marble-bookmatch-c3-wall-stn-marble-bookmatch-c4",
  "stn-marble-bookmatch-c3-wall-stn-taj-mahal-quartzite-c0",
  "stn-marble-bookmatch-c3-wall-stn-taj-mahal-quartzite-c1",
  "stn-marble-bookmatch-c3-wall-stn-taj-mahal-quartzite-c2",
  "stn-marble-bookmatch-c3-wall-stn-taj-mahal-quartzite-c3",
  "stn-marble-bookmatch-c3-wall-stn-taj-mahal-quartzite-c4",
  "stn-marble-bookmatch-c3-wall-stn-travertine-crazy-c0",
  "stn-marble-bookmatch-c3-wall-stn-travertine-crazy-c1",
  "stn-marble-bookmatch-c3-wall-stn-travertine-crazy-c2",
  "stn-marble-bookmatch-c3-wall-stn-travertine-crazy-c3",
  "stn-marble-bookmatch-c3-wall-stn-travertine-crazy-c4",
  "stn-marble-bookmatch-c3-wall-tle-botticino-c0",
  "stn-marble-bookmatch-c3-wall-tle-botticino-c1",
  "stn-marble-bookmatch-c3-wall-tle-botticino-c2",
  "stn-marble-bookmatch-c3-wall-tle-botticino-c3",
  "stn-marble-bookmatch-c3-wall-tle-botticino-c4",
  "stn-marble-bookmatch-c3-wall-tle-calacatta-slab-c0",
  "stn-marble-bookmatch-c3-wall-tle-calacatta-slab-c1",
  "stn-marble-bookmatch-c3-wall-tle-calacatta-slab-c2",
  "stn-marble-bookmatch-c3-wall-tle-calacatta-slab-c3",
  "stn-marble-bookmatch-c3-wall-tle-calacatta-slab-c4",
  "stn-marble-bookmatch-c3-wall-tle-concrete-porcelain-c0",
  "stn-marble-bookmatch-c3-wall-tle-concrete-porcelain-c1",
  "stn-marble-bookmatch-c3-wall-tle-concrete-porcelain-c2",
  "stn-marble-bookmatch-c3-wall-tle-concrete-porcelain-c3",
  "stn-marble-bookmatch-c3-wall-tle-concrete-porcelain-c4",
  "stn-marble-bookmatch-c3-wall-tle-herringbone-subway-c0",
  "stn-marble-bookmatch-c3-wall-tle-herringbone-subway-c1",
  "stn-marble-bookmatch-c3-wall-tle-herringbone-subway-c2",
  "stn-marble-bookmatch-c3-wall-tle-herringbone-subway-c3",
  "stn-marble-bookmatch-c3-wall-tle-herringbone-subway-c4",
  "stn-marble-bookmatch-c3-wall-tle-terrazzo-c0",
  "stn-marble-bookmatch-c3-wall-tle-terrazzo-c1",
  "stn-marble-bookmatch-c3-wall-tle-terrazzo-c2",
  "stn-marble-bookmatch-c3-wall-tle-terrazzo-c3",
  "stn-marble-bookmatch-c3-wall-tle-terrazzo-c4",
  "stn-marble-bookmatch-c3-wall-tle-zellige-c0",
  "stn-marble-bookmatch-c3-wall-tle-zellige-c1",
  "stn-marble-bookmatch-c3-wall-tle-zellige-c2",
  "stn-marble-bookmatch-c3-wall-tle-zellige-c3",
  "stn-marble-bookmatch-c3-wall-tle-zellige-c4",
  "stn-marble-bookmatch-c4-wall-stn-basaltina-c0",
  "stn-marble-bookmatch-c4-wall-stn-basaltina-c1",
  "stn-marble-bookmatch-c4-wall-stn-basaltina-c2",
  "stn-marble-bookmatch-c4-wall-stn-basaltina-c3",
  "stn-marble-bookmatch-c4-wall-stn-basaltina-c4",
  "stn-marble-bookmatch-c4-wall-stn-bluestone-c0",
  "stn-marble-bookmatch-c4-wall-stn-bluestone-c1",
  "stn-marble-bookmatch-c4-wall-stn-bluestone-c2",
  "stn-marble-bookmatch-c4-wall-stn-bluestone-c3",
  "stn-marble-bookmatch-c4-wall-stn-bluestone-c4",
  "stn-marble-bookmatch-c4-wall-stn-honed-limestone-c0",
  "stn-marble-bookmatch-c4-wall-stn-honed-limestone-c1",
  "stn-marble-bookmatch-c4-wall-stn-honed-limestone-c2",
  "stn-marble-bookmatch-c4-wall-stn-honed-limestone-c3",
  "stn-marble-bookmatch-c4-wall-stn-honed-limestone-c4",
  "stn-marble-bookmatch-c4-wall-stn-marble-bookmatch-c0",
  "stn-marble-bookmatch-c4-wall-stn-marble-bookmatch-c1",
  "stn-marble-bookmatch-c4-wall-stn-marble-bookmatch-c2",
  "stn-marble-bookmatch-c4-wall-stn-marble-bookmatch-c3",
  "stn-marble-bookmatch-c4-wall-stn-marble-bookmatch-c4",
  "stn-marble-bookmatch-c4-wall-stn-taj-mahal-quartzite-c0",
  "stn-marble-bookmatch-c4-wall-stn-taj-mahal-quartzite-c1",
  "stn-marble-bookmatch-c4-wall-stn-taj-mahal-quartzite-c2",
  "stn-marble-bookmatch-c4-wall-stn-taj-mahal-quartzite-c3",
  "stn-marble-bookmatch-c4-wall-stn-taj-mahal-quartzite-c4",
  "stn-marble-bookmatch-c4-wall-stn-travertine-crazy-c0",
  "stn-marble-bookmatch-c4-wall-stn-travertine-crazy-c1",
  "stn-marble-bookmatch-c4-wall-stn-travertine-crazy-c2",
  "stn-marble-bookmatch-c4-wall-stn-travertine-crazy-c3",
  "stn-marble-bookmatch-c4-wall-stn-travertine-crazy-c4",
  "stn-marble-bookmatch-c4-wall-tle-botticino-c0",
  "stn-marble-bookmatch-c4-wall-tle-botticino-c1",
  "stn-marble-bookmatch-c4-wall-tle-botticino-c2",
  "stn-marble-bookmatch-c4-wall-tle-botticino-c3",
  "stn-marble-bookmatch-c4-wall-tle-botticino-c4",
  "stn-marble-bookmatch-c4-wall-tle-calacatta-slab-c0",
  "stn-marble-bookmatch-c4-wall-tle-calacatta-slab-c1",
  "stn-marble-bookmatch-c4-wall-tle-calacatta-slab-c2",
  "stn-marble-bookmatch-c4-wall-tle-calacatta-slab-c3",
  "stn-marble-bookmatch-c4-wall-tle-calacatta-slab-c4",
  "stn-marble-bookmatch-c4-wall-tle-concrete-porcelain-c0",
  "stn-marble-bookmatch-c4-wall-tle-concrete-porcelain-c1",
  "stn-marble-bookmatch-c4-wall-tle-concrete-porcelain-c2",
  "stn-marble-bookmatch-c4-wall-tle-concrete-porcelain-c3",
  "stn-marble-bookmatch-c4-wall-tle-concrete-porcelain-c4",
  "stn-marble-bookmatch-c4-wall-tle-herringbone-subway-c0",
  "stn-marble-bookmatch-c4-wall-tle-herringbone-subway-c1",
  "stn-marble-bookmatch-c4-wall-tle-herringbone-subway-c2",
  "stn-marble-bookmatch-c4-wall-tle-herringbone-subway-c3",
  "stn-marble-bookmatch-c4-wall-tle-herringbone-subway-c4",
  "stn-marble-bookmatch-c4-wall-tle-terrazzo-c0",
  "stn-marble-bookmatch-c4-wall-tle-terrazzo-c1",
  "stn-marble-bookmatch-c4-wall-tle-terrazzo-c2",
  "stn-marble-bookmatch-c4-wall-tle-terrazzo-c3",
  "stn-marble-bookmatch-c4-wall-tle-terrazzo-c4",
  "stn-marble-bookmatch-c4-wall-tle-zellige-c0",
  "stn-marble-bookmatch-c4-wall-tle-zellige-c1",
  "stn-marble-bookmatch-c4-wall-tle-zellige-c2",
  "stn-marble-bookmatch-c4-wall-tle-zellige-c3",
  "stn-marble-bookmatch-c4-wall-tle-zellige-c4",
  "stn-taj-mahal-quartzite-c0-wall-stn-taj-mahal-quartzite-c1",
  "stn-taj-mahal-quartzite-c0-wall-stn-taj-mahal-quartzite-c2",
  "stn-taj-mahal-quartzite-c0-wall-stn-taj-mahal-quartzite-c3",
  "stn-taj-mahal-quartzite-c0-wall-stn-taj-mahal-quartzite-c4",
  "stn-taj-mahal-quartzite-c1-wall-stn-taj-mahal-quartzite-c0",
  "stn-taj-mahal-quartzite-c1-wall-stn-taj-mahal-quartzite-c1",
  "stn-taj-mahal-quartzite-c1-wall-stn-taj-mahal-quartzite-c2",
  "stn-taj-mahal-quartzite-c1-wall-stn-taj-mahal-quartzite-c3",
  "stn-taj-mahal-quartzite-c1-wall-stn-taj-mahal-quartzite-c4",
  "stn-taj-mahal-quartzite-c2-wall-stn-taj-mahal-quartzite-c0",
  "stn-taj-mahal-quartzite-c2-wall-stn-taj-mahal-quartzite-c1",
  "stn-taj-mahal-quartzite-c2-wall-stn-taj-mahal-quartzite-c2",
  "stn-taj-mahal-quartzite-c2-wall-stn-taj-mahal-quartzite-c3",
  "stn-taj-mahal-quartzite-c2-wall-stn-taj-mahal-quartzite-c4",
  "stn-taj-mahal-quartzite-c3-wall-stn-taj-mahal-quartzite-c0",
  "stn-taj-mahal-quartzite-c3-wall-stn-taj-mahal-quartzite-c1",
  "stn-taj-mahal-quartzite-c3-wall-stn-taj-mahal-quartzite-c2",
  "stn-taj-mahal-quartzite-c3-wall-stn-taj-mahal-quartzite-c3",
  "stn-taj-mahal-quartzite-c3-wall-stn-taj-mahal-quartzite-c4",
  "stn-taj-mahal-quartzite-c4-wall-stn-taj-mahal-quartzite-c0",
  "stn-taj-mahal-quartzite-c4-wall-stn-taj-mahal-quartzite-c1",
  "stn-taj-mahal-quartzite-c4-wall-stn-taj-mahal-quartzite-c2",
  "stn-taj-mahal-quartzite-c4-wall-stn-taj-mahal-quartzite-c3",
  "stn-taj-mahal-quartzite-c4-wall-stn-taj-mahal-quartzite-c4",
  "stn-travertine-crazy-c0-wall-stn-basaltina-c1",
  "stn-travertine-crazy-c0-wall-stn-basaltina-c2",
  "stn-travertine-crazy-c0-wall-stn-basaltina-c3",
  "stn-travertine-crazy-c0-wall-stn-basaltina-c4",
  "stn-travertine-crazy-c0-wall-stn-bluestone-c1",
  "stn-travertine-crazy-c0-wall-stn-bluestone-c2",
  "stn-travertine-crazy-c0-wall-stn-bluestone-c3",
  "stn-travertine-crazy-c0-wall-stn-bluestone-c4",
  "stn-travertine-crazy-c0-wall-stn-honed-limestone-c1",
  "stn-travertine-crazy-c0-wall-stn-honed-limestone-c2",
  "stn-travertine-crazy-c0-wall-stn-honed-limestone-c3",
  "stn-travertine-crazy-c0-wall-stn-honed-limestone-c4",
  "stn-travertine-crazy-c0-wall-stn-marble-bookmatch-c1",
  "stn-travertine-crazy-c0-wall-stn-marble-bookmatch-c2",
  "stn-travertine-crazy-c0-wall-stn-marble-bookmatch-c3",
  "stn-travertine-crazy-c0-wall-stn-marble-bookmatch-c4",
  "stn-travertine-crazy-c0-wall-stn-travertine-crazy-c1",
  "stn-travertine-crazy-c0-wall-stn-travertine-crazy-c2",
  "stn-travertine-crazy-c0-wall-stn-travertine-crazy-c3",
  "stn-travertine-crazy-c0-wall-stn-travertine-crazy-c4",
  "stn-travertine-crazy-c0-wall-tle-botticino-c1",
  "stn-travertine-crazy-c0-wall-tle-botticino-c2",
  "stn-travertine-crazy-c0-wall-tle-botticino-c3",
  "stn-travertine-crazy-c0-wall-tle-botticino-c4",
  "stn-travertine-crazy-c0-wall-tle-calacatta-slab-c1",
  "stn-travertine-crazy-c0-wall-tle-calacatta-slab-c2",
  "stn-travertine-crazy-c0-wall-tle-calacatta-slab-c3",
  "stn-travertine-crazy-c0-wall-tle-calacatta-slab-c4",
  "stn-travertine-crazy-c1-wall-stn-basaltina-c0",
  "stn-travertine-crazy-c1-wall-stn-basaltina-c1",
  "stn-travertine-crazy-c1-wall-stn-basaltina-c2",
  "stn-travertine-crazy-c1-wall-stn-basaltina-c3",
  "stn-travertine-crazy-c1-wall-stn-basaltina-c4",
  "stn-travertine-crazy-c1-wall-stn-bluestone-c0",
  "stn-travertine-crazy-c1-wall-stn-bluestone-c1",
  "stn-travertine-crazy-c1-wall-stn-bluestone-c2",
  "stn-travertine-crazy-c1-wall-stn-bluestone-c3",
  "stn-travertine-crazy-c1-wall-stn-bluestone-c4",
  "stn-travertine-crazy-c1-wall-stn-honed-limestone-c0",
  "stn-travertine-crazy-c1-wall-stn-honed-limestone-c1",
  "stn-travertine-crazy-c1-wall-stn-honed-limestone-c2",
  "stn-travertine-crazy-c1-wall-stn-honed-limestone-c3",
  "stn-travertine-crazy-c1-wall-stn-honed-limestone-c4",
  "stn-travertine-crazy-c1-wall-stn-marble-bookmatch-c0",
  "stn-travertine-crazy-c1-wall-stn-marble-bookmatch-c1",
  "stn-travertine-crazy-c1-wall-stn-marble-bookmatch-c2",
  "stn-travertine-crazy-c1-wall-stn-marble-bookmatch-c3",
  "stn-travertine-crazy-c1-wall-stn-marble-bookmatch-c4",
  "stn-travertine-crazy-c1-wall-stn-travertine-crazy-c0",
  "stn-travertine-crazy-c1-wall-stn-travertine-crazy-c1",
  "stn-travertine-crazy-c1-wall-stn-travertine-crazy-c2",
  "stn-travertine-crazy-c1-wall-stn-travertine-crazy-c3",
  "stn-travertine-crazy-c1-wall-stn-travertine-crazy-c4",
  "stn-travertine-crazy-c1-wall-tle-botticino-c0",
  "stn-travertine-crazy-c1-wall-tle-botticino-c1",
  "stn-travertine-crazy-c1-wall-tle-botticino-c2",
  "stn-travertine-crazy-c1-wall-tle-botticino-c3",
  "stn-travertine-crazy-c1-wall-tle-botticino-c4",
  "stn-travertine-crazy-c1-wall-tle-calacatta-slab-c0",
  "stn-travertine-crazy-c1-wall-tle-calacatta-slab-c1",
  "stn-travertine-crazy-c1-wall-tle-calacatta-slab-c2",
  "stn-travertine-crazy-c1-wall-tle-calacatta-slab-c3",
  "stn-travertine-crazy-c1-wall-tle-calacatta-slab-c4",
  "stn-travertine-crazy-c2-wall-stn-basaltina-c0",
  "stn-travertine-crazy-c2-wall-stn-basaltina-c1",
  "stn-travertine-crazy-c2-wall-stn-basaltina-c2",
  "stn-travertine-crazy-c2-wall-stn-basaltina-c3",
  "stn-travertine-crazy-c2-wall-stn-basaltina-c4",
  "stn-travertine-crazy-c2-wall-stn-bluestone-c0",
  "stn-travertine-crazy-c2-wall-stn-bluestone-c1",
  "stn-travertine-crazy-c2-wall-stn-bluestone-c2",
  "stn-travertine-crazy-c2-wall-stn-bluestone-c3",
  "stn-travertine-crazy-c2-wall-stn-bluestone-c4",
  "stn-travertine-crazy-c2-wall-stn-honed-limestone-c0",
  "stn-travertine-crazy-c2-wall-stn-honed-limestone-c1",
  "stn-travertine-crazy-c2-wall-stn-honed-limestone-c2",
  "stn-travertine-crazy-c2-wall-stn-honed-limestone-c3",
  "stn-travertine-crazy-c2-wall-stn-honed-limestone-c4",
  "stn-travertine-crazy-c2-wall-stn-marble-bookmatch-c0",
  "stn-travertine-crazy-c2-wall-stn-marble-bookmatch-c1",
  "stn-travertine-crazy-c2-wall-stn-marble-bookmatch-c2",
  "stn-travertine-crazy-c2-wall-stn-marble-bookmatch-c3",
  "stn-travertine-crazy-c2-wall-stn-marble-bookmatch-c4",
  "stn-travertine-crazy-c2-wall-stn-travertine-crazy-c0",
  "stn-travertine-crazy-c2-wall-stn-travertine-crazy-c1",
  "stn-travertine-crazy-c2-wall-stn-travertine-crazy-c2",
  "stn-travertine-crazy-c2-wall-stn-travertine-crazy-c3",
  "stn-travertine-crazy-c2-wall-stn-travertine-crazy-c4",
  "stn-travertine-crazy-c2-wall-tle-botticino-c0",
  "stn-travertine-crazy-c2-wall-tle-botticino-c1",
  "stn-travertine-crazy-c2-wall-tle-botticino-c2",
  "stn-travertine-crazy-c2-wall-tle-botticino-c3",
  "stn-travertine-crazy-c2-wall-tle-botticino-c4",
  "stn-travertine-crazy-c2-wall-tle-calacatta-slab-c0",
  "stn-travertine-crazy-c2-wall-tle-calacatta-slab-c1",
  "stn-travertine-crazy-c2-wall-tle-calacatta-slab-c2",
  "stn-travertine-crazy-c2-wall-tle-calacatta-slab-c3",
  "stn-travertine-crazy-c2-wall-tle-calacatta-slab-c4",
  "stn-travertine-crazy-c3-wall-stn-basaltina-c0",
  "stn-travertine-crazy-c3-wall-stn-basaltina-c1",
  "stn-travertine-crazy-c3-wall-stn-basaltina-c2",
  "stn-travertine-crazy-c3-wall-stn-basaltina-c3",
  "stn-travertine-crazy-c3-wall-stn-basaltina-c4",
  "stn-travertine-crazy-c3-wall-stn-bluestone-c0",
  "stn-travertine-crazy-c3-wall-stn-bluestone-c1",
  "stn-travertine-crazy-c3-wall-stn-bluestone-c2",
  "stn-travertine-crazy-c3-wall-stn-bluestone-c3",
  "stn-travertine-crazy-c3-wall-stn-bluestone-c4",
  "stn-travertine-crazy-c3-wall-stn-honed-limestone-c0",
  "stn-travertine-crazy-c3-wall-stn-honed-limestone-c1",
  "stn-travertine-crazy-c3-wall-stn-honed-limestone-c2",
  "stn-travertine-crazy-c3-wall-stn-honed-limestone-c3",
  "stn-travertine-crazy-c3-wall-stn-honed-limestone-c4",
  "stn-travertine-crazy-c3-wall-stn-marble-bookmatch-c0",
  "stn-travertine-crazy-c3-wall-stn-marble-bookmatch-c1",
  "stn-travertine-crazy-c3-wall-stn-marble-bookmatch-c2",
  "stn-travertine-crazy-c3-wall-stn-marble-bookmatch-c3",
  "stn-travertine-crazy-c3-wall-stn-marble-bookmatch-c4",
  "stn-travertine-crazy-c3-wall-stn-travertine-crazy-c0",
  "stn-travertine-crazy-c3-wall-stn-travertine-crazy-c1",
  "stn-travertine-crazy-c3-wall-stn-travertine-crazy-c2",
  "stn-travertine-crazy-c3-wall-stn-travertine-crazy-c3",
  "stn-travertine-crazy-c3-wall-stn-travertine-crazy-c4",
  "stn-travertine-crazy-c3-wall-tle-botticino-c0",
  "stn-travertine-crazy-c3-wall-tle-botticino-c1",
  "stn-travertine-crazy-c3-wall-tle-botticino-c2",
  "stn-travertine-crazy-c3-wall-tle-botticino-c3",
  "stn-travertine-crazy-c3-wall-tle-botticino-c4",
  "stn-travertine-crazy-c3-wall-tle-calacatta-slab-c0",
  "stn-travertine-crazy-c3-wall-tle-calacatta-slab-c1",
  "stn-travertine-crazy-c3-wall-tle-calacatta-slab-c2",
  "stn-travertine-crazy-c3-wall-tle-calacatta-slab-c3",
  "stn-travertine-crazy-c3-wall-tle-calacatta-slab-c4",
  "stn-travertine-crazy-c4-wall-stn-basaltina-c0",
  "stn-travertine-crazy-c4-wall-stn-basaltina-c1",
  "stn-travertine-crazy-c4-wall-stn-basaltina-c2",
  "stn-travertine-crazy-c4-wall-stn-basaltina-c3",
  "stn-travertine-crazy-c4-wall-stn-basaltina-c4",
  "stn-travertine-crazy-c4-wall-stn-bluestone-c0",
  "stn-travertine-crazy-c4-wall-stn-bluestone-c1",
  "stn-travertine-crazy-c4-wall-stn-bluestone-c2",
  "stn-travertine-crazy-c4-wall-stn-bluestone-c3",
  "stn-travertine-crazy-c4-wall-stn-bluestone-c4",
  "stn-travertine-crazy-c4-wall-stn-honed-limestone-c0",
  "stn-travertine-crazy-c4-wall-stn-honed-limestone-c1",
  "stn-travertine-crazy-c4-wall-stn-honed-limestone-c2",
  "stn-travertine-crazy-c4-wall-stn-honed-limestone-c3",
  "stn-travertine-crazy-c4-wall-stn-honed-limestone-c4",
  "stn-travertine-crazy-c4-wall-stn-marble-bookmatch-c0",
  "stn-travertine-crazy-c4-wall-stn-marble-bookmatch-c1",
  "stn-travertine-crazy-c4-wall-stn-marble-bookmatch-c2",
  "stn-travertine-crazy-c4-wall-stn-marble-bookmatch-c3",
  "stn-travertine-crazy-c4-wall-stn-marble-bookmatch-c4",
  "stn-travertine-crazy-c4-wall-stn-travertine-crazy-c0",
  "stn-travertine-crazy-c4-wall-stn-travertine-crazy-c1",
  "stn-travertine-crazy-c4-wall-stn-travertine-crazy-c2",
  "stn-travertine-crazy-c4-wall-stn-travertine-crazy-c3",
  "stn-travertine-crazy-c4-wall-stn-travertine-crazy-c4",
  "stn-travertine-crazy-c4-wall-tle-botticino-c0",
  "stn-travertine-crazy-c4-wall-tle-botticino-c1",
  "stn-travertine-crazy-c4-wall-tle-botticino-c2",
  "stn-travertine-crazy-c4-wall-tle-botticino-c3",
  "stn-travertine-crazy-c4-wall-tle-botticino-c4",
  "stn-travertine-crazy-c4-wall-tle-calacatta-slab-c0",
  "stn-travertine-crazy-c4-wall-tle-calacatta-slab-c1",
  "stn-travertine-crazy-c4-wall-tle-calacatta-slab-c2",
  "stn-travertine-crazy-c4-wall-tle-calacatta-slab-c3",
  "stn-travertine-crazy-c4-wall-tle-calacatta-slab-c4",
  "tle-botticino-c0-wall-stn-honed-limestone-c1",
  "tle-botticino-c0-wall-stn-honed-limestone-c2",
  "tle-botticino-c0-wall-stn-honed-limestone-c3",
  "tle-botticino-c0-wall-stn-honed-limestone-c4",
  "tle-botticino-c0-wall-stn-travertine-crazy-c1",
  "tle-botticino-c0-wall-stn-travertine-crazy-c2",
  "tle-botticino-c0-wall-stn-travertine-crazy-c3",
  "tle-botticino-c0-wall-stn-travertine-crazy-c4",
  "tle-botticino-c0-wall-tle-botticino-c1",
  "tle-botticino-c0-wall-tle-botticino-c2",
  "tle-botticino-c0-wall-tle-botticino-c3",
  "tle-botticino-c0-wall-tle-botticino-c4",
  "tle-botticino-c0-wall-tle-calacatta-slab-c1",
  "tle-botticino-c0-wall-tle-calacatta-slab-c2",
  "tle-botticino-c0-wall-tle-calacatta-slab-c3",
  "tle-botticino-c0-wall-tle-calacatta-slab-c4",
  "tle-botticino-c1-wall-stn-honed-limestone-c0",
  "tle-botticino-c1-wall-stn-honed-limestone-c1",
  "tle-botticino-c1-wall-stn-honed-limestone-c2",
  "tle-botticino-c1-wall-stn-honed-limestone-c3",
  "tle-botticino-c1-wall-stn-honed-limestone-c4",
  "tle-botticino-c1-wall-stn-travertine-crazy-c0",
  "tle-botticino-c1-wall-stn-travertine-crazy-c1",
  "tle-botticino-c1-wall-stn-travertine-crazy-c2",
  "tle-botticino-c1-wall-stn-travertine-crazy-c3",
  "tle-botticino-c1-wall-stn-travertine-crazy-c4",
  "tle-botticino-c1-wall-tle-botticino-c0",
  "tle-botticino-c1-wall-tle-botticino-c1",
  "tle-botticino-c1-wall-tle-botticino-c2",
  "tle-botticino-c1-wall-tle-botticino-c3",
  "tle-botticino-c1-wall-tle-botticino-c4",
  "tle-botticino-c1-wall-tle-calacatta-slab-c0",
  "tle-botticino-c1-wall-tle-calacatta-slab-c1",
  "tle-botticino-c1-wall-tle-calacatta-slab-c2",
  "tle-botticino-c1-wall-tle-calacatta-slab-c3",
  "tle-botticino-c1-wall-tle-calacatta-slab-c4",
  "tle-botticino-c2-wall-stn-honed-limestone-c0",
  "tle-botticino-c2-wall-stn-honed-limestone-c1",
  "tle-botticino-c2-wall-stn-honed-limestone-c2",
  "tle-botticino-c2-wall-stn-honed-limestone-c3",
  "tle-botticino-c2-wall-stn-honed-limestone-c4",
  "tle-botticino-c2-wall-stn-travertine-crazy-c0",
  "tle-botticino-c2-wall-stn-travertine-crazy-c1",
  "tle-botticino-c2-wall-stn-travertine-crazy-c2",
  "tle-botticino-c2-wall-stn-travertine-crazy-c3",
  "tle-botticino-c2-wall-stn-travertine-crazy-c4",
  "tle-botticino-c2-wall-tle-botticino-c0",
  "tle-botticino-c2-wall-tle-botticino-c1",
  "tle-botticino-c2-wall-tle-botticino-c2",
  "tle-botticino-c2-wall-tle-botticino-c3",
  "tle-botticino-c2-wall-tle-botticino-c4",
  "tle-botticino-c2-wall-tle-calacatta-slab-c0",
  "tle-botticino-c2-wall-tle-calacatta-slab-c1",
  "tle-botticino-c2-wall-tle-calacatta-slab-c2",
  "tle-botticino-c2-wall-tle-calacatta-slab-c3",
  "tle-botticino-c2-wall-tle-calacatta-slab-c4",
  "tle-botticino-c3-wall-stn-honed-limestone-c0",
  "tle-botticino-c3-wall-stn-honed-limestone-c1",
  "tle-botticino-c3-wall-stn-honed-limestone-c2",
  "tle-botticino-c3-wall-stn-honed-limestone-c3",
  "tle-botticino-c3-wall-stn-honed-limestone-c4",
  "tle-botticino-c3-wall-stn-travertine-crazy-c0",
  "tle-botticino-c3-wall-stn-travertine-crazy-c1",
  "tle-botticino-c3-wall-stn-travertine-crazy-c2",
  "tle-botticino-c3-wall-stn-travertine-crazy-c3",
  "tle-botticino-c3-wall-stn-travertine-crazy-c4",
  "tle-botticino-c3-wall-tle-botticino-c0",
  "tle-botticino-c3-wall-tle-botticino-c1",
  "tle-botticino-c3-wall-tle-botticino-c2",
  "tle-botticino-c3-wall-tle-botticino-c3",
  "tle-botticino-c3-wall-tle-botticino-c4",
  "tle-botticino-c3-wall-tle-calacatta-slab-c0",
  "tle-botticino-c3-wall-tle-calacatta-slab-c1",
  "tle-botticino-c3-wall-tle-calacatta-slab-c2",
  "tle-botticino-c3-wall-tle-calacatta-slab-c3",
  "tle-botticino-c3-wall-tle-calacatta-slab-c4",
  "tle-botticino-c4-wall-stn-honed-limestone-c0",
  "tle-botticino-c4-wall-stn-honed-limestone-c1",
  "tle-botticino-c4-wall-stn-honed-limestone-c2",
  "tle-botticino-c4-wall-stn-honed-limestone-c3",
  "tle-botticino-c4-wall-stn-honed-limestone-c4",
  "tle-botticino-c4-wall-stn-travertine-crazy-c0",
  "tle-botticino-c4-wall-stn-travertine-crazy-c1",
  "tle-botticino-c4-wall-stn-travertine-crazy-c2",
  "tle-botticino-c4-wall-stn-travertine-crazy-c3",
  "tle-botticino-c4-wall-stn-travertine-crazy-c4",
  "tle-botticino-c4-wall-tle-botticino-c0",
  "tle-botticino-c4-wall-tle-botticino-c1",
  "tle-botticino-c4-wall-tle-botticino-c2",
  "tle-botticino-c4-wall-tle-botticino-c3",
  "tle-botticino-c4-wall-tle-botticino-c4",
  "tle-botticino-c4-wall-tle-calacatta-slab-c0",
  "tle-botticino-c4-wall-tle-calacatta-slab-c1",
  "tle-botticino-c4-wall-tle-calacatta-slab-c2",
  "tle-botticino-c4-wall-tle-calacatta-slab-c3",
  "tle-botticino-c4-wall-tle-calacatta-slab-c4",
  "tle-calacatta-slab-c0-wall-stn-honed-limestone-c1",
  "tle-calacatta-slab-c0-wall-stn-honed-limestone-c2",
  "tle-calacatta-slab-c0-wall-stn-honed-limestone-c3",
  "tle-calacatta-slab-c0-wall-stn-honed-limestone-c4",
  "tle-calacatta-slab-c0-wall-stn-travertine-crazy-c1",
  "tle-calacatta-slab-c0-wall-stn-travertine-crazy-c2",
  "tle-calacatta-slab-c0-wall-stn-travertine-crazy-c3",
  "tle-calacatta-slab-c0-wall-stn-travertine-crazy-c4",
  "tle-calacatta-slab-c0-wall-tle-botticino-c1",
  "tle-calacatta-slab-c0-wall-tle-botticino-c2",
  "tle-calacatta-slab-c0-wall-tle-botticino-c3",
  "tle-calacatta-slab-c0-wall-tle-botticino-c4",
  "tle-calacatta-slab-c0-wall-tle-calacatta-slab-c1",
  "tle-calacatta-slab-c0-wall-tle-calacatta-slab-c2",
  "tle-calacatta-slab-c0-wall-tle-calacatta-slab-c3",
  "tle-calacatta-slab-c0-wall-tle-calacatta-slab-c4",
  "tle-calacatta-slab-c1-wall-stn-honed-limestone-c0",
  "tle-calacatta-slab-c1-wall-stn-honed-limestone-c1",
  "tle-calacatta-slab-c1-wall-stn-honed-limestone-c2",
  "tle-calacatta-slab-c1-wall-stn-honed-limestone-c3",
  "tle-calacatta-slab-c1-wall-stn-honed-limestone-c4",
  "tle-calacatta-slab-c1-wall-stn-travertine-crazy-c0",
  "tle-calacatta-slab-c1-wall-stn-travertine-crazy-c1",
  "tle-calacatta-slab-c1-wall-stn-travertine-crazy-c2",
  "tle-calacatta-slab-c1-wall-stn-travertine-crazy-c3",
  "tle-calacatta-slab-c1-wall-stn-travertine-crazy-c4",
  "tle-calacatta-slab-c1-wall-tle-botticino-c0",
  "tle-calacatta-slab-c1-wall-tle-botticino-c1",
  "tle-calacatta-slab-c1-wall-tle-botticino-c2",
  "tle-calacatta-slab-c1-wall-tle-botticino-c3",
  "tle-calacatta-slab-c1-wall-tle-botticino-c4",
  "tle-calacatta-slab-c1-wall-tle-calacatta-slab-c0",
  "tle-calacatta-slab-c1-wall-tle-calacatta-slab-c1",
  "tle-calacatta-slab-c1-wall-tle-calacatta-slab-c2",
  "tle-calacatta-slab-c1-wall-tle-calacatta-slab-c3",
  "tle-calacatta-slab-c1-wall-tle-calacatta-slab-c4",
  "tle-calacatta-slab-c2-wall-stn-honed-limestone-c0",
  "tle-calacatta-slab-c2-wall-stn-honed-limestone-c1",
  "tle-calacatta-slab-c2-wall-stn-honed-limestone-c2",
  "tle-calacatta-slab-c2-wall-stn-honed-limestone-c3",
  "tle-calacatta-slab-c2-wall-stn-honed-limestone-c4",
  "tle-calacatta-slab-c2-wall-stn-travertine-crazy-c0",
  "tle-calacatta-slab-c2-wall-stn-travertine-crazy-c1",
  "tle-calacatta-slab-c2-wall-stn-travertine-crazy-c2",
  "tle-calacatta-slab-c2-wall-stn-travertine-crazy-c3",
  "tle-calacatta-slab-c2-wall-stn-travertine-crazy-c4",
  "tle-calacatta-slab-c2-wall-tle-botticino-c0",
  "tle-calacatta-slab-c2-wall-tle-botticino-c1",
  "tle-calacatta-slab-c2-wall-tle-botticino-c2",
  "tle-calacatta-slab-c2-wall-tle-botticino-c3",
  "tle-calacatta-slab-c2-wall-tle-botticino-c4",
  "tle-calacatta-slab-c2-wall-tle-calacatta-slab-c0",
  "tle-calacatta-slab-c2-wall-tle-calacatta-slab-c1",
  "tle-calacatta-slab-c2-wall-tle-calacatta-slab-c2",
  "tle-calacatta-slab-c2-wall-tle-calacatta-slab-c3",
  "tle-calacatta-slab-c2-wall-tle-calacatta-slab-c4",
  "tle-calacatta-slab-c3-wall-stn-honed-limestone-c0",
  "tle-calacatta-slab-c3-wall-stn-honed-limestone-c1",
  "tle-calacatta-slab-c3-wall-stn-honed-limestone-c2",
  "tle-calacatta-slab-c3-wall-stn-honed-limestone-c3",
  "tle-calacatta-slab-c3-wall-stn-honed-limestone-c4",
  "tle-calacatta-slab-c3-wall-stn-travertine-crazy-c0",
  "tle-calacatta-slab-c3-wall-stn-travertine-crazy-c1",
  "tle-calacatta-slab-c3-wall-stn-travertine-crazy-c2",
  "tle-calacatta-slab-c3-wall-stn-travertine-crazy-c3",
  "tle-calacatta-slab-c3-wall-stn-travertine-crazy-c4",
  "tle-calacatta-slab-c3-wall-tle-botticino-c0",
  "tle-calacatta-slab-c3-wall-tle-botticino-c1",
  "tle-calacatta-slab-c3-wall-tle-botticino-c2",
  "tle-calacatta-slab-c3-wall-tle-botticino-c3",
  "tle-calacatta-slab-c3-wall-tle-botticino-c4",
  "tle-calacatta-slab-c3-wall-tle-calacatta-slab-c0",
  "tle-calacatta-slab-c3-wall-tle-calacatta-slab-c1",
  "tle-calacatta-slab-c3-wall-tle-calacatta-slab-c2",
  "tle-calacatta-slab-c3-wall-tle-calacatta-slab-c3",
  "tle-calacatta-slab-c3-wall-tle-calacatta-slab-c4",
  "tle-calacatta-slab-c4-wall-stn-honed-limestone-c0",
  "tle-calacatta-slab-c4-wall-stn-honed-limestone-c1",
  "tle-calacatta-slab-c4-wall-stn-honed-limestone-c2",
  "tle-calacatta-slab-c4-wall-stn-honed-limestone-c3",
  "tle-calacatta-slab-c4-wall-stn-honed-limestone-c4",
  "tle-calacatta-slab-c4-wall-stn-travertine-crazy-c0",
  "tle-calacatta-slab-c4-wall-stn-travertine-crazy-c1",
  "tle-calacatta-slab-c4-wall-stn-travertine-crazy-c2",
  "tle-calacatta-slab-c4-wall-stn-travertine-crazy-c3",
  "tle-calacatta-slab-c4-wall-stn-travertine-crazy-c4",
  "tle-calacatta-slab-c4-wall-tle-botticino-c0",
  "tle-calacatta-slab-c4-wall-tle-botticino-c1",
  "tle-calacatta-slab-c4-wall-tle-botticino-c2",
  "tle-calacatta-slab-c4-wall-tle-botticino-c3",
  "tle-calacatta-slab-c4-wall-tle-botticino-c4",
  "tle-calacatta-slab-c4-wall-tle-calacatta-slab-c0",
  "tle-calacatta-slab-c4-wall-tle-calacatta-slab-c1",
  "tle-calacatta-slab-c4-wall-tle-calacatta-slab-c2",
  "tle-calacatta-slab-c4-wall-tle-calacatta-slab-c3",
  "tle-calacatta-slab-c4-wall-tle-calacatta-slab-c4",
  "tle-concrete-porcelain-c0-wall-tle-concrete-porcelain-c1",
  "tle-concrete-porcelain-c0-wall-tle-concrete-porcelain-c2",
  "tle-concrete-porcelain-c0-wall-tle-concrete-porcelain-c3",
  "tle-concrete-porcelain-c0-wall-tle-concrete-porcelain-c4",
  "tle-concrete-porcelain-c1-wall-tle-concrete-porcelain-c0",
  "tle-concrete-porcelain-c1-wall-tle-concrete-porcelain-c1",
  "tle-concrete-porcelain-c1-wall-tle-concrete-porcelain-c2",
  "tle-concrete-porcelain-c1-wall-tle-concrete-porcelain-c3",
  "tle-concrete-porcelain-c1-wall-tle-concrete-porcelain-c4",
  "tle-concrete-porcelain-c2-wall-tle-concrete-porcelain-c0",
  "tle-concrete-porcelain-c2-wall-tle-concrete-porcelain-c1",
  "tle-concrete-porcelain-c2-wall-tle-concrete-porcelain-c2",
  "tle-concrete-porcelain-c2-wall-tle-concrete-porcelain-c3",
  "tle-concrete-porcelain-c2-wall-tle-concrete-porcelain-c4",
  "tle-concrete-porcelain-c3-wall-tle-concrete-porcelain-c0",
  "tle-concrete-porcelain-c3-wall-tle-concrete-porcelain-c1",
  "tle-concrete-porcelain-c3-wall-tle-concrete-porcelain-c2",
  "tle-concrete-porcelain-c3-wall-tle-concrete-porcelain-c3",
  "tle-concrete-porcelain-c3-wall-tle-concrete-porcelain-c4",
  "tle-concrete-porcelain-c4-wall-tle-concrete-porcelain-c0",
  "tle-concrete-porcelain-c4-wall-tle-concrete-porcelain-c1",
  "tle-concrete-porcelain-c4-wall-tle-concrete-porcelain-c2",
  "tle-concrete-porcelain-c4-wall-tle-concrete-porcelain-c3",
  "tle-concrete-porcelain-c4-wall-tle-concrete-porcelain-c4",
  "tle-herringbone-subway-c0-wall-tle-herringbone-subway-c1",
  "tle-herringbone-subway-c0-wall-tle-herringbone-subway-c2",
  "tle-herringbone-subway-c0-wall-tle-herringbone-subway-c3",
  "tle-herringbone-subway-c0-wall-tle-herringbone-subway-c4",
  "tle-herringbone-subway-c1-wall-tle-herringbone-subway-c0",
  "tle-herringbone-subway-c1-wall-tle-herringbone-subway-c1",
  "tle-herringbone-subway-c1-wall-tle-herringbone-subway-c2",
  "tle-herringbone-subway-c1-wall-tle-herringbone-subway-c3",
  "tle-herringbone-subway-c1-wall-tle-herringbone-subway-c4",
  "tle-herringbone-subway-c2-wall-tle-herringbone-subway-c0",
  "tle-herringbone-subway-c2-wall-tle-herringbone-subway-c1",
  "tle-herringbone-subway-c2-wall-tle-herringbone-subway-c2",
  "tle-herringbone-subway-c2-wall-tle-herringbone-subway-c3",
  "tle-herringbone-subway-c2-wall-tle-herringbone-subway-c4",
  "tle-herringbone-subway-c3-wall-tle-herringbone-subway-c0",
  "tle-herringbone-subway-c3-wall-tle-herringbone-subway-c1",
  "tle-herringbone-subway-c3-wall-tle-herringbone-subway-c2",
  "tle-herringbone-subway-c3-wall-tle-herringbone-subway-c3",
  "tle-herringbone-subway-c3-wall-tle-herringbone-subway-c4",
  "tle-herringbone-subway-c4-wall-tle-herringbone-subway-c0",
  "tle-herringbone-subway-c4-wall-tle-herringbone-subway-c1",
  "tle-herringbone-subway-c4-wall-tle-herringbone-subway-c2",
  "tle-herringbone-subway-c4-wall-tle-herringbone-subway-c3",
  "tle-herringbone-subway-c4-wall-tle-herringbone-subway-c4",
  "tle-terrazzo-c0-wall-tle-terrazzo-c1",
  "tle-terrazzo-c0-wall-tle-terrazzo-c2",
  "tle-terrazzo-c0-wall-tle-terrazzo-c3",
  "tle-terrazzo-c0-wall-tle-terrazzo-c4",
  "tle-terrazzo-c1-wall-tle-terrazzo-c0",
  "tle-terrazzo-c1-wall-tle-terrazzo-c1",
  "tle-terrazzo-c1-wall-tle-terrazzo-c2",
  "tle-terrazzo-c1-wall-tle-terrazzo-c3",
  "tle-terrazzo-c1-wall-tle-terrazzo-c4",
  "tle-terrazzo-c2-wall-tle-terrazzo-c0",
  "tle-terrazzo-c2-wall-tle-terrazzo-c1",
  "tle-terrazzo-c2-wall-tle-terrazzo-c2",
  "tle-terrazzo-c2-wall-tle-terrazzo-c3",
  "tle-terrazzo-c2-wall-tle-terrazzo-c4",
  "tle-terrazzo-c3-wall-tle-terrazzo-c0",
  "tle-terrazzo-c3-wall-tle-terrazzo-c1",
  "tle-terrazzo-c3-wall-tle-terrazzo-c2",
  "tle-terrazzo-c3-wall-tle-terrazzo-c3",
  "tle-terrazzo-c3-wall-tle-terrazzo-c4",
  "tle-terrazzo-c4-wall-tle-terrazzo-c0",
  "tle-terrazzo-c4-wall-tle-terrazzo-c1",
  "tle-terrazzo-c4-wall-tle-terrazzo-c2",
  "tle-terrazzo-c4-wall-tle-terrazzo-c3",
  "tle-terrazzo-c4-wall-tle-terrazzo-c4",
  "tle-zellige-c0-wall-tle-zellige-c1",
  "tle-zellige-c0-wall-tle-zellige-c2",
  "tle-zellige-c0-wall-tle-zellige-c3",
  "tle-zellige-c0-wall-tle-zellige-c4",
  "tle-zellige-c1-wall-tle-zellige-c0",
  "tle-zellige-c1-wall-tle-zellige-c1",
  "tle-zellige-c1-wall-tle-zellige-c2",
  "tle-zellige-c1-wall-tle-zellige-c3",
  "tle-zellige-c1-wall-tle-zellige-c4",
  "tle-zellige-c2-wall-tle-zellige-c0",
  "tle-zellige-c2-wall-tle-zellige-c1",
  "tle-zellige-c2-wall-tle-zellige-c2",
  "tle-zellige-c2-wall-tle-zellige-c3",
  "tle-zellige-c2-wall-tle-zellige-c4",
  "tle-zellige-c3-wall-tle-zellige-c0",
  "tle-zellige-c3-wall-tle-zellige-c1",
  "tle-zellige-c3-wall-tle-zellige-c2",
  "tle-zellige-c3-wall-tle-zellige-c3",
  "tle-zellige-c3-wall-tle-zellige-c4",
  "tle-zellige-c4-wall-tle-zellige-c0",
  "tle-zellige-c4-wall-tle-zellige-c1",
  "tle-zellige-c4-wall-tle-zellige-c2",
  "tle-zellige-c4-wall-tle-zellige-c3",
  "tle-zellige-c4-wall-tle-zellige-c4",
]);
